import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SocketService } from '../../game/services/SocketService';
import { HttpService } from '../../game/services/HttpService';
import { TexasHoldemGame } from '../../game/models/TexasHoldemGame';
import { ServerMessages } from '../../game/constants/ServerMessages';
import { UserMessages } from '../../game/constants/UserMessages';
import { PlayerHandActions } from '../../game/constants/PlayerHandActions';
import { Card, Player, Seat, PlayerReadyForSeating, SocketMessage } from '../../game/types';
import { getSafeCardImagePath, parseCard, cardToBestHandFormat } from '../../game/utils/cardUtils';
// generateUUID removed - room creation now in Dashboard
import './Game.component.css';

/** Returns a consistent color for a given string (e.g. player name) so each user has a unique color in chat. */
function colorForChatName(name: string): string {
  let hash = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 75%, 55%)`;
}

// Global connection state to prevent multiple connection attempts across remounts
const globalConnectionState = {
  isConnecting: false,
  isConnected: false,
  socketService: null as SocketService | null,
  connectionTime: 0,
  mountCount: 0,
  firstMountTime: 0,
};

const RESIZE_DEBOUNCE_MS = 200;
const BREAKPOINT_TABLET = 768;
const BREAKPOINT_DESKTOP = 968;

function getViewportSizeMode(width: number): 'mobile' | 'tablet' | 'desktop' {
  if (width <= BREAKPOINT_TABLET) return 'mobile';
  if (width <= BREAKPOINT_DESKTOP) return 'tablet';
  return 'desktop';
}

const Game: React.FC = () => {
  const [viewportSizeMode, setViewportSizeMode] = useState<'mobile' | 'tablet' | 'desktop'>(() =>
    getViewportSizeMode(typeof window !== 'undefined' ? window.innerWidth : 1024)
  );
  const isDesktopWindowSize = viewportSizeMode === 'desktop';
  const isTabletWindowSize = viewportSizeMode === 'tablet';
  const isMobileWindowSize = viewportSizeMode === 'mobile';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const socketServiceRef = useRef<SocketService | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Delay connect until after Strict Mode unmount
  const httpServiceRef = useRef<HttpService | null>(null);
  const gameRef = useRef<TexasHoldemGame | null>(null);
  const mainPlayerIdRef = useRef<string>('');
  const isConnectingRef = useRef<boolean>(false);
  const hasConnectedRef = useRef<boolean>(false);
  const hasJoinedRoomRef = useRef<boolean>(false);
  const handlersSetupForSocketRef = useRef<SocketService | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentPlayerIndexRef = useRef<number | null>(null);
  const isMyTurnRef = useRef<boolean>(false);
  const turnSoundEnabledRef = useRef<boolean>(true);

  const [gameState, setGameState] = useState<any>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [tableCards, setTableCards] = useState<Card[]>([]);
  const [pot, setPot] = useState(0);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState<number | null>(null);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [raiseAmount, setRaiseAmount] = useState(0);
  const [highestBet, setHighestBet] = useState(0);
  // firstBetAmount removed - using value from message directly
  const [playerLastRoundBet, setPlayerLastRoundBet] = useState(0);
  const [noBetYet, setNoBetYet] = useState(false);
  const [minRaiseAmount, setMinRaiseAmount] = useState(0);
  const [maxRaiseAmount, setMaxRaiseAmount] = useState(0);
  const [timer, setTimer] = useState(0);
  const [mainPlayerId, setMainPlayerId] = useState<string>('');
  const [roomId, setRoomId] = useState<string>('');
  const [winMessage, setWinMessage] = useState<string | null>(null);
  const [winningPlayerId, setWinningPlayerId] = useState<string | null>(null);
  const [winningCards, setWinningCards] = useState<string[]>([]);
  const [notEnoughPlayersMessage, setNotEnoughPlayersMessage] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** Observers at the table; updated from OBSERVERS_LIST / OBSERVER_JOINED / OBSERVER_LEFT */
  const [observers, setObservers] = useState<Array<{ playerId: string; playerName: string }>>([]);
  const [observersListOpen, setObserversListOpen] = useState(true);
  /** True when current user is observing only (no seat); can rejoin via JOIN_AS_PLAYER */
  const [isObserver, setIsObserver] = useState(false);
  /** When observer clicks an empty seat: seatIndex to show join modal; null = closed */
  const [joinSeatModal, setJoinSeatModal] = useState<{ seatIndex: number } | null>(null);
  /** Glow empty seats when observer first joins so they see they can click to join; cleared after delay or when they open join modal */
  const [showObserverJoinGlow, setShowObserverJoinGlow] = useState(false);
  /** Chip amount selected in join-seat modal (clamped to table min/max) */
  const [joinSeatChips, setJoinSeatChips] = useState(1000);
  /** Table chip limits for join modal (from START_GAME_JOINED_PLAYER / GAME_START_UI) */
  const [tableMinChips, setTableMinChips] = useState(1000);
  const [tableMaxChips, setTableMaxChips] = useState(10000);
  const [showRevealModalEnabled, setShowRevealModalEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('gameRevealModalEnabled');
      return stored !== 'false';
    } catch {
      return true;
    }
  });
  const [showTableRevealEnabled, setShowTableRevealEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('gameTableRevealEnabled');
      return stored !== 'false';
    } catch {
      return true;
    }
  });
  const [turnSoundEnabled, setTurnSoundEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('gameTurnSoundEnabled');
      return stored !== 'false';
    } catch {
      return true;
    }
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [standingSecondsByPlayer, setStandingSecondsByPlayer] = useState<Record<string, number>>({});
  /** Disconnected grace countdown: playerId -> seconds until sit-out. Shown as "Disconnected · 0:45" on seat. */
  const [disconnectGraceSecondsByPlayer, setDisconnectGraceSecondsByPlayer] = useState<Record<string, number>>({});
  const standingPlayerIdsRef = useRef<string[]>([]);
  const notEnoughPlayersRef = useRef(false);
  const isObserverRef = useRef(false);
  const lobbyWindowRef = useRef<Window | null>(null);
  const [gameHeldMessage, setGameHeldMessage] = useState<string | null>(null);
  /** When set, table will close after the current hand (e.g. open 12+ hours). Shown as a banner. */
  const [tableClosingAfterHandMessage, setTableClosingAfterHandMessage] = useState<string | null>(null);
  /** Closable error/message modal (replaces alert). When set, navigateToDashboardOnClose means close -> navigate to dashboard (e.g. room not found). */
  const [errorModal, setErrorModal] = useState<{ message: string; navigateToDashboardOnClose?: boolean } | null>(null);
  const celebrationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bustedMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCelebrationAtRef = useRef<number>(0);
  const CELEBRATION_DEBOUNCE_MS = 600;
  const pageHiddenAtRef = useRef<number | null>(null);
  const MIN_HIDDEN_MS_BEFORE_RECONNECT = 1500;
  const [gameChatMessages, setGameChatMessages] = useState<Array<{ playerName: string; message: string }>>([]);
  const [gameChatInput, setGameChatInput] = useState('');
  const [chatPanelOpen, setChatPanelOpen] = useState(true);
  const [chatPanelMinimized, setChatPanelMinimized] = useState(true);
  const [chatHasUnread, setChatHasUnread] = useState(false);
  const gameChatMessagesRef = useRef<HTMLDivElement>(null);
  /** After hand ends, players can optionally reveal cards; cleared on ALLOCATE_CARDS. */
  const [revealedCardsByPlayer, setRevealedCardsByPlayer] = useState<Record<string, (Card | null)[]>>({});
  /** Only cards revealed by the user via CARD_REVEALED (not showdown); used for transition/glow. Cleared on ALLOCATE_CARDS. */
  const [userRevealedCardsByPlayer, setUserRevealedCardsByPlayer] = useState<Record<string, (Card | null)[]>>({});
  /** True after SHOW_ALL_PLAYERS_IN_GAME_CARDS this hand; cleared on ALLOCATE_CARDS. Used to hide reveal modal for winner once showdown is shown. */
  const [showdownCardsShownThisHand, setShowdownCardsShownThisHand] = useState(false);
  /** True only when winner is celebrating (CELEBRATE_AND_PAY_WINNER); cleared on ALLOCATE_CARDS. Used to show face-down placeholders and "Reveal" button for remaining table cards. */
  const [showRevealTableCardsOption, setShowRevealTableCardsOption] = useState(false);

  const ACTION_BAR_STORAGE_KEY = 'gameActionBarPosition';
  const CHAT_PANEL_EXPANDED_STORAGE_KEY = 'gameChatPositionExpanded';
  const CHAT_PANEL_MINIMIZED_STORAGE_KEY = 'gameChatPositionMinimized';
  const REVEAL_MODAL_STORAGE_KEY = 'gameRevealModalPosition';
  const REVEAL_MODAL_ENABLED_STORAGE_KEY = 'gameRevealModalEnabled';
  const TABLE_REVEAL_ENABLED_STORAGE_KEY = 'gameTableRevealEnabled';
  const TURN_SOUND_ENABLED_STORAGE_KEY = 'gameTurnSoundEnabled';

  turnSoundEnabledRef.current = turnSoundEnabled;

  const playTurnSound = useCallback(() => {
    if (!turnSoundEnabledRef.current) return;
    try {
      const audio = new Audio('/sounds/turn-notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch {
      // ignore
    }
  }, []);

  const getDefaultActionBarPos = (): { left: number; top: number } => {
    if (typeof window === 'undefined') return { left: 0, top: 0 };
    return { left: Math.max(0, window.innerWidth - 420), top: Math.max(0, window.innerHeight - 100) };
  };
  const getDefaultChatExpandedPos = (): { left: number; top: number } => {
    if (typeof window === 'undefined') return { left: 0, top: 0 };
    return { left: Math.max(0, window.innerWidth - 340), top: Math.max(0, window.innerHeight - 310) };
  };
  const getDefaultChatMinimizedPos = (): { left: number; top: number } => {
    if (typeof window === 'undefined') return { left: 0, top: 0 };
    return { left: Math.max(0, window.innerWidth - 72), top: Math.max(0, window.innerHeight - 80) };
  };
  const getDefaultRevealModalPos = (): { left: number; top: number } => {
    if (typeof window === 'undefined') return { left: 0, top: 0 };
    return { left: Math.max(0, (window.innerWidth - 280) / 2), top: Math.max(0, (window.innerHeight - 200) / 2) };
  };
  const loadPosition = (key: string, defaultPos: () => { left: number; top: number }): { left: number; top: number } => {
    try {
      const s = localStorage.getItem(key);
      if (s) {
        const p = JSON.parse(s) as { left?: number; top?: number };
        if (typeof p.left === 'number' && typeof p.top === 'number') return { left: p.left, top: p.top };
      }
    } catch (_) {}
    return defaultPos();
  };
  const [actionBarPos, setActionBarPos] = useState<{ left: number; top: number }>(() => loadPosition(ACTION_BAR_STORAGE_KEY, getDefaultActionBarPos));
  const [chatPanelPosExpanded, setChatPanelPosExpanded] = useState<{ left: number; top: number }>(() => loadPosition(CHAT_PANEL_EXPANDED_STORAGE_KEY, getDefaultChatExpandedPos));
  const [chatPanelPosMinimized, setChatPanelPosMinimized] = useState<{ left: number; top: number }>(() => loadPosition(CHAT_PANEL_MINIMIZED_STORAGE_KEY, getDefaultChatMinimizedPos));
  const [revealModalPos, setRevealModalPos] = useState<{ left: number; top: number }>(() => loadPosition(REVEAL_MODAL_STORAGE_KEY, getDefaultRevealModalPos));
  const dragStateRef = useRef<{
    which: 'action' | 'chat' | 'reveal';
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
    lastLeft: number;
    lastTop: number;
  } | null>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const revealModalRef = useRef<HTMLDivElement>(null);
  const chatJustDraggedRef = useRef(false);
  const raiseAmountShouldResetToMinRef = useRef(false);

  const ACTION_BAR_W = 400;
  const ACTION_BAR_H = 80;
  const CHAT_PANEL_W = 320;
  const CHAT_PANEL_H = 280;
  const CHAT_MINIMIZED_W = 72;
  const CHAT_MINIMIZED_H = 44;
  const REVEAL_MODAL_W = 280;
  const REVEAL_MODAL_H = 200;

  const clampPosition = (left: number, top: number, w: number, h: number) => ({
    left: Math.max(0, Math.min(window.innerWidth - w, left)),
    top: Math.max(0, Math.min(window.innerHeight - h, top)),
  });

  const chatPanelMinimizedRef = useRef(chatPanelMinimized);
  chatPanelMinimizedRef.current = chatPanelMinimized;

  const handleDragStart = useCallback((which: 'action' | 'chat' | 'reveal', clientX: number, clientY: number) => {
    const minimized = chatPanelMinimizedRef.current;
    const pos = which === 'action' ? actionBarPos
      : which === 'reveal' ? revealModalPos
      : (minimized ? chatPanelPosMinimized : chatPanelPosExpanded);
    dragStateRef.current = {
      which,
      startX: clientX,
      startY: clientY,
      startLeft: pos.left,
      startTop: pos.top,
      lastLeft: pos.left,
      lastTop: pos.top,
    };
    const onMove = (e: PointerEvent) => {
      const d = dragStateRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const newLeft = d.startLeft + dx;
      const newTop = d.startTop + dy;
      const minRef = chatPanelMinimizedRef.current;
      let w: number, h: number;
      if (d.which === 'action') {
        const el = actionBarRef.current;
        w = el ? el.offsetWidth : ACTION_BAR_W;
        h = el ? el.offsetHeight : ACTION_BAR_H;
      } else if (d.which === 'reveal') {
        w = REVEAL_MODAL_W;
        h = REVEAL_MODAL_H;
      } else {
        w = minRef ? CHAT_MINIMIZED_W : CHAT_PANEL_W;
        h = minRef ? CHAT_MINIMIZED_H : CHAT_PANEL_H;
      }
      const clamped = clampPosition(newLeft, newTop, w, h);
      d.lastLeft = clamped.left;
      d.lastTop = clamped.top;
      if (d.which === 'action') setActionBarPos(clamped);
      else if (d.which === 'reveal') setRevealModalPos(clamped);
      else (minRef ? setChatPanelPosMinimized : setChatPanelPosExpanded)(clamped);
    };
    const onUp = () => {
      const d = dragStateRef.current;
      if (!d) return;
      if (d.which === 'action') {
        localStorage.setItem(ACTION_BAR_STORAGE_KEY, JSON.stringify({ left: d.lastLeft, top: d.lastTop }));
      } else if (d.which === 'reveal') {
        localStorage.setItem(REVEAL_MODAL_STORAGE_KEY, JSON.stringify({ left: d.lastLeft, top: d.lastTop }));
      } else {
        const key = chatPanelMinimizedRef.current ? CHAT_PANEL_MINIMIZED_STORAGE_KEY : CHAT_PANEL_EXPANDED_STORAGE_KEY;
        localStorage.setItem(key, JSON.stringify({ left: d.lastLeft, top: d.lastTop }));
        chatJustDraggedRef.current = true;
      }
      dragStateRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [actionBarPos, chatPanelPosExpanded, chatPanelPosMinimized, revealModalPos]);

  useEffect(() => {
    const onResize = () => {
      setActionBarPos((prev) => {
        const el = actionBarRef.current;
        const w = el ? el.offsetWidth : ACTION_BAR_W;
        const h = el ? el.offsetHeight : ACTION_BAR_H;
        const c = clampPosition(prev.left, prev.top, w, h);
        if (c.left !== prev.left || c.top !== prev.top) {
          localStorage.setItem(ACTION_BAR_STORAGE_KEY, JSON.stringify(c));
          return c;
        }
        return prev;
      });
      setChatPanelPosExpanded((prev) => {
        const c = clampPosition(prev.left, prev.top, CHAT_PANEL_W, CHAT_PANEL_H);
        if (c.left !== prev.left || c.top !== prev.top) {
          localStorage.setItem(CHAT_PANEL_EXPANDED_STORAGE_KEY, JSON.stringify(c));
          return c;
        }
        return prev;
      });
      setChatPanelPosMinimized((prev) => {
        const c = clampPosition(prev.left, prev.top, CHAT_MINIMIZED_W, CHAT_MINIMIZED_H);
        if (c.left !== prev.left || c.top !== prev.top) {
          localStorage.setItem(CHAT_PANEL_MINIMIZED_STORAGE_KEY, JSON.stringify(c));
          return c;
        }
        return prev;
      });
      setRevealModalPos((prev) => {
        const c = clampPosition(prev.left, prev.top, REVEAL_MODAL_W, REVEAL_MODAL_H);
        if (c.left !== prev.left || c.top !== prev.top) {
          localStorage.setItem(REVEAL_MODAL_STORAGE_KEY, JSON.stringify(c));
          return c;
        }
        return prev;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Initialize auth info and roomId - separate effect like Lobby component
  useEffect(() => {
    const token = localStorage.getItem('token') || searchParams.get('token');
    const userId = localStorage.getItem('userId') || searchParams.get('userId');
    const username = localStorage.getItem('username') || searchParams.get('username');
    let roomIdParam = searchParams.get('roomId');

    if (!token || !userId || !username) {
      return;
    }

    // Store auth info in localStorage if it came from URL params (for game window)
    if (searchParams.get('token') && !localStorage.getItem('token')) {
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      localStorage.setItem('username', username);
    }

    setMainPlayerId(userId);
    mainPlayerIdRef.current = userId;

    // Allow rejoin on refresh: if no roomId in URL but we have one in localStorage, redirect to same page with roomId
    if (!roomIdParam) {
      const savedRoomId = localStorage.getItem('currentRoomId');
      if (savedRoomId) {
        console.log('No roomId in URL but currentRoomId in localStorage, redirecting to rejoin:', savedRoomId);
        navigate(`/game?roomId=${savedRoomId}`, { replace: true });
        return;
      }
      console.log('No roomId in URL or localStorage, redirecting to dashboard');
      navigate('/dashboard');
      return;
    }

    setRoomId(roomIdParam);

    // Cleanup: disconnect only. Do NOT send EXIT_GAME here – that would fold/sit out on refresh.
    // Server treats disconnect as temporary (handlePlayerDisconnected) so player can rejoin.
    return () => {
      if (socketServiceRef.current) {
        console.log('Cleaning up socket connection');
        socketServiceRef.current.disconnect();
        socketServiceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Separate effect to handle WebSocket connection and room joining - like Lobby component
  useEffect(() => {
    const roomIdParam = searchParams.get('roomId');
    const token = localStorage.getItem('token') || searchParams.get('token');
    const userId = localStorage.getItem('userId') || searchParams.get('userId');
    const username = localStorage.getItem('username') || searchParams.get('username');

    if (!token || !userId || !username) {
      return;
    }

    if (!roomIdParam) {
      return;
    }

    // Don't create a new connection if one already exists
    if (socketServiceRef.current && socketServiceRef.current.isConnected()) {
      return;
    }

    // Delay connect so Strict Mode's first unmount clears this timeout; only the final mount connects (one attempt)
    const connectAndJoin = async () => {
      try {
        // Clean up any existing connection first
        if (socketServiceRef.current) {
          socketServiceRef.current.disconnect();
          socketServiceRef.current = null;
        }

        handlersSetupForSocketRef.current = null;

        const socketService = new SocketService();
        socketServiceRef.current = socketService;
        globalConnectionState.socketService = socketService;
        globalConnectionState.isConnecting = true;

        await socketService.connect(token, 'game');

        // If ref was cleared, cleanup ran (e.g. Strict Mode unmount) – don't attach handlers or join room
        if (socketServiceRef.current !== socketService) {
          socketService.disconnect();
          isConnectingRef.current = false;
          hasConnectedRef.current = false;
          globalConnectionState.socketService = null;
          globalConnectionState.isConnecting = false;
          globalConnectionState.isConnected = false;
          return;
        }

        // Set up message handlers (only once per connection)
        setupSocketHandlers(socketService, userId, username);

        // Initialize HTTP service
        const httpService = new HttpService();
        httpService.setToken(token);
        httpServiceRef.current = httpService;

        // Wait a moment for handlers to be set up, then join room
        const observeOnly = searchParams.get('observeOnly') === '1' || searchParams.get('observeOnly') === 'true';
        setTimeout(() => {
          if (socketService.isConnected()) {
            console.log('Sending JOIN_ROOM for roomId:', roomIdParam, 'observeOnly:', observeOnly);
            socketService.sendMessage({
              type: UserMessages.JOIN_ROOM,
              roomId: roomIdParam,
              playerId: userId,
              playerName: username,
              token: token,
              observeOnly: observeOnly,
            });
            hasJoinedRoomRef.current = true;
            hasConnectedRef.current = true;
            isConnectingRef.current = false;
            globalConnectionState.isConnected = true;
            globalConnectionState.isConnecting = false;
          } else {
            console.error('WebSocket not connected, cannot join room');
          }
        }, 500);
      } catch (error) {
        console.error('Failed to connect WebSocket:', { error });
        isConnectingRef.current = false;
        hasConnectedRef.current = false;
        globalConnectionState.isConnecting = false;
        globalConnectionState.isConnected = false;
      }
    };

    connectTimeoutRef.current = setTimeout(() => {
      connectTimeoutRef.current = null;
      connectAndJoin();
    }, 150);

    // Do NOT send EXIT_GAME on beforeunload/cleanup – refresh would fold/sit out the player.
    // Server treats socket close as disconnect (rejoin allowed).

    return () => {
      if (connectTimeoutRef.current != null) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
        celebrationTimeoutRef.current = null;
      }
      if (bustedMessageTimeoutRef.current) {
        clearTimeout(bustedMessageTimeoutRef.current);
        bustedMessageTimeoutRef.current = null;
      }
      if (socketServiceRef.current) {
        console.log('Cleaning up socket connection');
        socketServiceRef.current.disconnect();
        socketServiceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Re-run when searchParams changes

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        const next = getViewportSizeMode(window.innerWidth);
        setViewportSizeMode((prev) => (prev !== next ? next : prev));
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // When user returns to the tab/app (e.g. from background on phone), reconnect socket if it was hidden long enough,
  // and always request full game state so the UI is up to date (players, sit-outs, turn, timer, etc.).
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pageHiddenAtRef.current = Date.now();
      } else {
        const hiddenAt = pageHiddenAtRef.current;
        pageHiddenAtRef.current = null;
        const socket = socketServiceRef.current;
        const currentRoomId = roomId || (typeof window !== 'undefined' ? localStorage.getItem('currentRoomId') : null);
        const currentPlayerId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
        if (socket?.isConnected() && currentRoomId && currentPlayerId) {
          if (hiddenAt != null) {
            const hiddenDuration = Date.now() - hiddenAt;
            if (hiddenDuration >= MIN_HIDDEN_MS_BEFORE_RECONNECT) {
              console.log('Page visible again after being hidden – reconnecting socket so messages are received');
              socket.forceReconnect().then(() => {
                setTimeout(() => requestGameState(), 300);
              }).catch((err) => console.error('Visibility reconnect failed:', err));
              return;
            }
          }
          requestGameState();
        }
      }
    };
    function requestGameState() {
      const socket = socketServiceRef.current;
      const currentRoomId = roomId || (typeof window !== 'undefined' ? localStorage.getItem('currentRoomId') : null);
      const currentPlayerId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      if (socket?.isConnected() && currentRoomId && currentPlayerId) {
        socket.sendMessage({
          type: UserMessages.REQUEST_GAME_STATE,
          roomId: currentRoomId,
          playerId: currentPlayerId,
        });
      }
    }
    let lastFocusRequest = 0;
    const MIN_FOCUS_REQUEST_MS = 2000;
    const handleWindowFocus = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastFocusRequest < MIN_FOCUS_REQUEST_MS) return;
      lastFocusRequest = now;
      const currentRoomId = roomId || (typeof window !== 'undefined' ? localStorage.getItem('currentRoomId') : null);
      const currentPlayerId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      if (socketServiceRef.current?.isConnected() && currentRoomId && currentPlayerId) requestGameState();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [roomId]);

  // Auto-scroll game chat to bottom when new messages arrive
  useEffect(() => {
    const el = gameChatMessagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [gameChatMessages]);

  // When user opens (expands) the chat, scroll to show the last message
  useEffect(() => {
    if (!chatPanelMinimized) {
      const el = gameChatMessagesRef.current;
      if (el) {
        const scrollToBottom = () => { el.scrollTop = el.scrollHeight; };
        requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
      }
    }
  }, [chatPanelMinimized]);

  // Fullscreen: sync state with browser fullscreen API
  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Escape key exits fullscreen
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  }, []);

  const handleServerMessage = useCallback((message: SocketMessage, userId: string, username: string) => {
    console.log('=== Received message ===');
    console.log('Message type:', message.type);
    console.log('Full message:', JSON.stringify(message, null, 2));
    console.log('=======================');

    switch (message.type) {
      case ServerMessages.ROOM_CREATED:
      case ServerMessages.ROOM_JOINED:
        console.log('ROOM_CREATED or ROOM_JOINED received:', message);
        console.log('isRunning:', message.isRunning, 'isObserver:', message.isObserver);
        if (message.roomId) {
          setRoomId(message.roomId);
          localStorage.setItem('currentRoomId', message.roomId);
        }
        if (message.isObserver === true) {
          isObserverRef.current = true;
          setIsObserver(true);
        }
        if (message.observers && Array.isArray(message.observers)) {
          setObservers((message.observers as Array<{ playerId: string; playerName: string }>).slice(0, 50));
        }
        if (message.isRunning) {
          if (gameRef.current) {
            // START_GAME_JOINED_PLAYER was already processed (e.g. arrived before ROOM_JOINED), no need to wait
            if ((window as any).__startGameJoinedPlayerTimeout) {
              clearTimeout((window as any).__startGameJoinedPlayerTimeout);
              delete (window as any).__startGameJoinedPlayerTimeout;
            }
          } else {
            console.log('Game is running, waiting for START_GAME_JOINED_PLAYER message');
            const timeoutId = setTimeout(() => {
              if (!gameRef.current) {
                console.error('ERROR: START_GAME_JOINED_PLAYER not received after 3 seconds!');
                console.error('Game state is still null - cannot display game table');
                console.error('This means the backend did not send START_GAME_JOINED_PLAYER');
              }
            }, 3000);
            (window as any).__startGameJoinedPlayerTimeout = timeoutId;
          }
        }
        break;
      case ServerMessages.PLAYER_JOINED:
        // Player joined - no lobby UI to update (lobby is in separate component)
        console.log('PLAYER_JOINED:', message);
        break;
      case ServerMessages.CHAT_MESSAGE:
        // Chat messages handled in lobby, not in game table
        break;
      case ServerMessages.PLAYER_LEFT_ROOM:
        // Player left - no lobby UI to update
        break;
      case ServerMessages.OBSERVERS_LIST:
        if (message.observers && Array.isArray(message.observers)) {
          setObservers((message.observers as Array<{ playerId: string; playerName: string }>).slice(0, 50));
        }
        break;
      case ServerMessages.OBSERVER_JOINED:
        if (message.playerId && message.playerName) {
          setObservers((prev) => {
            const id = String(message.playerId);
            if (prev.some((o) => o.playerId === id)) return prev;
            return [...prev, { playerId: id, playerName: String(message.playerName) }];
          });
        }
        break;
      case ServerMessages.OBSERVER_LEFT:
        if (message.playerId) {
          setObservers((prev) => prev.filter((o) => o.playerId !== String(message.playerId)));
        }
        break;
      case ServerMessages.PLAYER_BUSTED: {
        const bustedId = message.playerId ? String(message.playerId) : null;
        const seatIndexFromMsg = typeof message.seatIndex === 'number' ? message.seatIndex : undefined;
        if (bustedId === userId) {
          isObserverRef.current = true;
          setIsObserver(true);
          setWinMessage("You're out of chips. You can keep watching and rejoin when a seat is free.");
          if (bustedMessageTimeoutRef.current) clearTimeout(bustedMessageTimeoutRef.current);
          bustedMessageTimeoutRef.current = setTimeout(() => {
            setWinMessage(null);
            bustedMessageTimeoutRef.current = null;
          }, 5000);
          stopTimer();
          setCurrentPlayerIndex(null);
          currentPlayerIndexRef.current = null;
        }
        if (bustedId) {
          setObservers((prev) => {
            if (prev.some((o) => o.playerId === bustedId)) return prev;
            return [...prev, { playerId: bustedId, playerName: String(message.playerName || 'Player') }];
          });
          setPlayers((prev) => prev.filter((p) => p.uuid !== bustedId));
          setSeats((prev) => {
            const seatIdx = seatIndexFromMsg ?? prev.find((s) => s.player?.uuid === bustedId)?.seatIndex;
            if (typeof seatIdx === 'number' && seatIdx >= 0) {
              return prev.map((s) => (s.seatIndex === seatIdx ? { ...s, isOccupied: false, player: undefined } : s));
            }
            return prev;
          });
        }
        break;
      }
      case ServerMessages.HOST_PLAYER_CHANGED:
        // Host changed - no lobby UI to update
        break;
      case ServerMessages.START_GAME_JOINED_PLAYER:
        console.log('START_GAME_JOINED_PLAYER received, joining game in progress:', message);
        console.log('Full message:', JSON.stringify(message, null, 2));
        // Clear the timeout if it exists
        if ((window as any).__startGameJoinedPlayerTimeout) {
          clearTimeout((window as any).__startGameJoinedPlayerTimeout);
          delete (window as any).__startGameJoinedPlayerTimeout;
        }
        // Player is joining a game that's already running - initialize game immediately
        handleStartGameJoinedPlayer(message, userId, username);
        break;
      case ServerMessages.START_GAME:
      case ServerMessages.GAME_START_UI:
        console.log('GAME_START_UI/START_GAME received, initializing game:', message);

        // Get game config from message
        const smallBlind = message.smallBlind || 10;
        const bigBlind = message.bigBlind || 20;
        const chips = message.minEntryChips || 1000;
        setTableMinChips(message.minEntryChips || chips);
        setTableMaxChips(message.maxEntryChips || chips);

        const config = {
          smallBlind: smallBlind,
          bigBlind: bigBlind,
          numberOfPlayers: message.numberOfPlayers || 9,
          minEntryChips: message.minEntryChips || chips,
          maxEntryChips: message.maxEntryChips || chips,
          allowedNumberOfCardsPerHand: message.allowedNumberOfCardsPerHand || 2,
        };

        const game = new TexasHoldemGame(config, message.gameId || '');
        game.setMainInfo(userId, username, message.roomId || roomId || '');

        // Create players from message.playersInfo or lobbyData
        let playersReady: PlayerReadyForSeating[] = [];

        if (message.playersInfo) {
          // Java format: playersInfo is an object with playerId as keys
          const playersInfo = message.playersInfo as Record<string, { name: string; seatIndex: number; chips?: number }>;
          playersReady = Object.entries(playersInfo).map(([playerId, info]) => ({
            playerId: playerId,
            playerName: info.name,
            seatIndex: info.seatIndex,
            chips: info.chips || chips,
          }));
        } else if (message.playersReadyForSeating) {
          // Direct array format
          playersReady = message.playersReadyForSeating as PlayerReadyForSeating[];
        }

        console.log('Initializing game with players:', playersReady);

        if (playersReady.length > 0) {
          game.initiateGame(playersReady);

          const gameState = game.getGameState();
          console.log('Game state initialized:', gameState);

          // Store game in ref immediately
          gameRef.current = game;

          // Set all game state and clear lobby
          setGameState(gameState);
          setPlayers(gameState.players);
          setSeats(gameState.seats);
          if (message.roomId || roomId) {
            const finalRoomId = message.roomId || roomId;
            setRoomId(finalRoomId);
            // Store roomId in localStorage so we can reconnect on page refresh
            localStorage.setItem('currentRoomId', finalRoomId);
          }
        } else {
          console.warn('No players available to initialize game');
        }
        break;
      case ServerMessages.ADD_PLAYER_TO_GAME:
        handleAddPlayerToGame(message);
        break;
      case ServerMessages.ALLOCATE_CARDS:
        setGameHeldMessage(null);
        setNotEnoughPlayersMessage(null);
        notEnoughPlayersRef.current = false;
        setRevealedCardsByPlayer({});
        setUserRevealedCardsByPlayer({});
        setShowdownCardsShownThisHand(false);
        setShowRevealTableCardsOption(false);
        raiseAmountShouldResetToMinRef.current = true;
        handleAllocateCards(message);
        break;
      case ServerMessages.DEAL_CARDS:
        handleDealCards(message, userId);
        break;
      case ServerMessages.NOTIFY_PLAYERS_TO_START_THEIR_TIMER:
        if (!notEnoughPlayersRef.current) handleStartTimer(message);
        break;
      case ServerMessages.TURN_AND_TIMER_SYNC:
        if (!notEnoughPlayersRef.current) handleTurnAndTimerSync(message);
        break;
      case ServerMessages.PLAYER_FOLDED:
        handlePlayerFolded(message);
        break;
      case ServerMessages.PLAYER_CALLED:
      case ServerMessages.PLAYER_RAISED:
        handlePlayerAction(message);
        break;
      case ServerMessages.DEAL_TABLE_CARDS:
        // New street = new round of betting; reset raise slider/amount to minimum when user next acts
        raiseAmountShouldResetToMinRef.current = true;
        handleDealTableCards(message);
        break;
      case ServerMessages.REFRESH_TABLE_POT_AND_RESET_PLAYERS_ACTIONS:
        // End of betting round; next action (if any) will be in new round — reset raise to min
        raiseAmountShouldResetToMinRef.current = true;
        handleRefreshPot(message);
        break;
      case ServerMessages.CELEBRATE_AND_PAY_WINNER:
        setShowRevealTableCardsOption(true);
        handleCelebrateAndPayWinner(message);
        break;
      case ServerMessages.REVEAL_TABLE_CARDS:
        handleRevealTableCards(message);
        break;
      case ServerMessages.PAY_PLAYER_ROUND_BET_DIFFERENCE:
        handlePayPlayerRoundBetDifference(message);
        break;
      case ServerMessages.SHOW_ALL_PLAYERS_IN_GAME_CARDS:
        handleShowAllCards(message);
        break;
      case ServerMessages.CARD_REVEALED:
        handleCardRevealed(message);
        break;
      case ServerMessages.GAME_NOT_ENOUGH_PLAYERS:
        handleGameNotEnoughPlayers(message);
        break;
      case ServerMessages.GAME_ENDED:
        handleGameEnded(message);
        break;
      case ServerMessages.TABLE_CLOSING_AFTER_HAND: {
        const reason = (message.reason as string) || 'This table has been open for over 12 hours and will close after this hand. Finish the hand, then the table and lobby will close.';
        setTableClosingAfterHandMessage(reason);
        break;
      }
      case ServerMessages.ROOM_DELETED: {
        const deletedRoomId = message?.roomId as string | undefined;
        const currentRoom = roomId || (typeof window !== 'undefined' ? localStorage.getItem('currentRoomId') : null);
        if (deletedRoomId && deletedRoomId === currentRoom) {
          setTableClosingAfterHandMessage(null);
          setErrorModal({
            message: 'This game has been closed (no players seated).',
            navigateToDashboardOnClose: true,
          });
          const closeOrNavigate = () => {
            if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
              window.close();
            } else {
              navigate('/dashboard', { replace: true });
            }
          };
          setTimeout(closeOrNavigate, 2000);
        }
        break;
      }
      case ServerMessages.PLAYER_EXITED:
        handlePlayerExited(message);
        break;
      case ServerMessages.PLAYER_SIT_OUT:
        handlePlayerSitOut(message);
        break;
      case ServerMessages.PLAYER_STANDING:
        if (message.playerId && message.secondsRemaining != null) {
          setStandingSecondsByPlayer((prev) => ({ ...prev, [message.playerId as string]: message.secondsRemaining as number }));
          setPlayers((prev) => prev.map((p) => p.uuid === message.playerId ? { ...p, hand: [] } : p));
        }
        break;
      case ServerMessages.PLAYER_SAT:
        if (message.playerId) {
          setStandingSecondsByPlayer((prev) => {
            const next = { ...prev };
            delete next[message.playerId as string];
            return next;
          });
          setGameHeldMessage(null);
        }
        break;
      case ServerMessages.STANDING_COUNTDOWN:
        if (message.standingSecondsRemaining && typeof message.standingSecondsRemaining === 'object') {
          setStandingSecondsByPlayer(message.standingSecondsRemaining as Record<string, number>);
        }
        break;
      case ServerMessages.DISCONNECT_GRACE_COUNTDOWN:
        if (message.disconnectGraceSecondsRemaining && typeof message.disconnectGraceSecondsRemaining === 'object') {
          setDisconnectGraceSecondsByPlayer(message.disconnectGraceSecondsRemaining as Record<string, number>);
        }
        break;
      case ServerMessages.GAME_HELD_WAITING_FOR_SEAT:
        setGameHeldMessage((message.message as string) || 'Waiting for a player to sit back.');
        break;
      case ServerMessages.CHAT_MESSAGE_GAME:
        setGameChatMessages((prev) => [
          ...prev,
          {
            playerName: (message.playerName as string) || 'Unknown',
            message: (message.message as string) || '',
          },
        ]);
        if (chatPanelMinimizedRef.current) setChatHasUnread(true);
        break;
      case ServerMessages.ERROR_MESSAGE:
        console.error('Server error:', message.message);
        const errorMsg = (message.message as string) || 'An error occurred';
        // Check if error is authentication-related
        const lowerErrorMsg = errorMsg.toLowerCase();
        if (lowerErrorMsg.includes('token') ||
          lowerErrorMsg.includes('unauthorized') ||
          lowerErrorMsg.includes('authentication') ||
          (lowerErrorMsg.includes('invalid') && lowerErrorMsg.includes('session'))) {
          // Clear invalid token and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('emailVerified');
          window.location.href = '/login';
        } else {
          const isRoomOrGameNotFound =
            lowerErrorMsg.includes('room not found') ||
            lowerErrorMsg.includes('game not found') ||
            lowerErrorMsg.includes('not a player in this room');
          setErrorModal({
            message: errorMsg,
            navigateToDashboardOnClose: isRoomOrGameNotFound,
          });
        }
        break;
      case ServerMessages.NORMAL_MESSAGE:
      case ServerMessages.WARNING_MESSAGE:
        // Handle informational messages if needed
        console.log('Server message:', message.message);
        break;
      default:
        console.log('Unhandled message type:', message.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupSocketHandlers = useCallback((socketService: SocketService, userId: string, username: string) => {
    console.log('=== setupSocketHandlers CALLED ===', {
      timestamp: Date.now(),
      userId,
      username,
      handlersAlreadySetup: handlersSetupForSocketRef.current === socketService,
      socketServiceInstance: socketService.toString(),
    });

    // Prevent duplicate handler setup for the same socket instance
    if (handlersSetupForSocketRef.current === socketService) {
      console.log('Handlers already set up for this socket instance, skipping duplicate setup');
      return;
    }

    console.log('Setting up socket handlers for user', { userId, username });

    // Clear any existing handlers first to prevent duplicates
    socketService.clearAllHandlers();
    console.log('Cleared all existing handlers');

    // Handle all server messages
    Object.values(ServerMessages).forEach((messageType) => {
      socketService.onMessage(messageType, (message: SocketMessage) => {
        console.debug(`Handler called for message type: ${messageType}`, { messageType, message });
        handleServerMessage(message, userId, username);
      });
    });
    console.log('Socket handlers set up', {
      handlerCount: Object.values(ServerMessages).length,
      messageTypes: Object.values(ServerMessages),
    });

    // Mark handlers as set up for this specific socket instance
    handlersSetupForSocketRef.current = socketService;
    console.log('Handlers marked as set up for socket instance');

    // Set up reconnection handler to re-register handlers
    const reconnectHandler = () => {
      console.log('Reconnection detected, re-registering handlers');
      handlersSetupForSocketRef.current = null; // Reset flag to allow re-registration
      setupSocketHandlers(socketService, userId, username);

      // Rejoin room if we have a roomId
      const currentRoomId = localStorage.getItem('currentRoomId');
      if (currentRoomId && socketService.isConnected()) {
        const token = localStorage.getItem('token') || '';
        const username = localStorage.getItem('username') || 'Player';
        console.log('Rejoining room after reconnection', { currentRoomId });
        socketService.sendMessage({
          type: UserMessages.JOIN_ROOM,
          roomId: currentRoomId,
          playerId: userId,
          playerName: username,
          token: token,
        });
      }
    };
    socketService.onReconnect(reconnectHandler);
    console.log('Reconnection handler set up');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleServerMessage]);

  // Room creation/joining handlers removed - now handled in Dashboard and Lobby components


  // Helper function to stop the timer
  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setTimer(0);
    setIsMyTurn(false);
    isMyTurnRef.current = false;
    setCurrentPlayerIndex(null);
    currentPlayerIndexRef.current = null;
  }, []);

  // Reset game state before a new game starts (similar to Java's preGameRest)
  const resetGameState = useCallback(() => {
    console.log('Resetting game state for new game');

    // Stop timer when resetting game state
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setTimer(0);
    setIsMyTurn(false);
    isMyTurnRef.current = false;
    setCurrentPlayerIndex(null);
    currentPlayerIndexRef.current = null;

    // Clear table cards
    setTableCards([]);

    // Clear all player hands and reset folded state for new hand.
    // Preserve main player's hand if they have cards (e.g. folded player) so reveal modal still works until DEAL_CARDS.
    const mainId = mainPlayerIdRef.current || '';
    setPlayers((prevPlayers) => {
      return prevPlayers.map((p) => {
        const keepHandForReveal = p.uuid === mainId && (p.hand?.length ?? 0) > 0;
        return {
          ...p,
          hand: keepHandForReveal ? p.hand! : [],
          isFolded: keepHandForReveal ? p.isFolded : false,
        };
      });
    });

    // Clear seat indicators (blinds, button); keep "Sitting Out" and "Fold" until new hand is dealt (they get overwritten in handleAllocateCards for seats that get blind/button)
    setSeats((prevSeats) => {
      return prevSeats.map((seat) => ({
        ...seat,
        isButton: false,
        isSmallBlind: false,
        isBigBlind: false,
        actionText: (seat.actionText === 'Sitting Out' || seat.actionText === 'Fold') ? seat.actionText : undefined,
      }));
    });

    // Reset game state variables
    setPot(0);
    setHighestBet(0);
    setPlayerLastRoundBet(0);
    setNoBetYet(false);
    setCurrentPlayerIndex(null);
    setIsMyTurn(false);
    isMyTurnRef.current = false;
    setTimer(0);
    setRaiseAmount(0);
  }, []);

  const handleAllocateCards = (message: SocketMessage) => {
    if (!gameRef.current) return;

    console.log('handleAllocateCards called with:', message);

    // Reset game state first (clear table cards, player hands, blinds, button)
    resetGameState();

    const playersUpdates = message.playersUpdates || [];
    const standingIdsFromMessage: string[] = Array.isArray(message.standingPlayerIds) ? (message.standingPlayerIds as string[]) : [];

    // Get blind amounts from gameState or defaults
    let smallBlindAmount = 10;
    let bigBlindAmount = 20;

    if (gameState && gameState.config) {
      smallBlindAmount = gameState.config.smallBlind || 10;
      bigBlindAmount = gameState.config.bigBlind || 20;
    }

    // Update both players and seats together to ensure references are correct
    setPlayers((prevPlayers) => {
      console.log('Current players in handleAllocateCards:', prevPlayers);
      const updatedPlayers = prevPlayers.map((p) => ({ ...p }));

      playersUpdates.forEach((update: any) => {
        const playerIndex = updatedPlayers.findIndex((p) => p.uuid === update.playerId);
        if (playerIndex !== -1) {
          // Update player chips
          if (update.playerChips !== undefined) {
            updatedPlayers[playerIndex] = {
              ...updatedPlayers[playerIndex],
              chips: update.playerChips,
            };
          }
        } else {
          console.warn('Player not found for update:', update.playerId);
        }
      });

      // Update seats with the updated players
      setSeats((prevSeats) => {
        const updatedSeats = prevSeats.map((s) => ({ ...s }));

        playersUpdates.forEach((update: any) => {
          const player = updatedPlayers.find((p) => p.uuid === update.playerId);
          if (player) {
            const seatIndex = player.seatIndex ?? 0;
            if (updatedSeats[seatIndex]) {
              const wasSittingOut = updatedSeats[seatIndex].actionText === 'Sitting Out';
              // Clear all indicators first, then set new ones; never overwrite "Sitting Out"
              updatedSeats[seatIndex].isButton = false;
              updatedSeats[seatIndex].isSmallBlind = false;
              updatedSeats[seatIndex].isBigBlind = false;
              if (!wasSittingOut) updatedSeats[seatIndex].actionText = undefined;

              // Update seat indicators based on message
              if (update.setBigBlindImage === 'true' || update.setBigBlindImage === true) {
                updatedSeats[seatIndex].isBigBlind = true;
                if (!wasSittingOut) updatedSeats[seatIndex].actionText = `$${bigBlindAmount}`;
              }
              if (update.setSmallBlindImage === 'true' || update.setSmallBlindImage === true) {
                updatedSeats[seatIndex].isSmallBlind = true;
                if (!wasSittingOut) updatedSeats[seatIndex].actionText = `$${smallBlindAmount}`;
              }
              if (update.setButtonImage === 'true' || update.setButtonImage === true) {
                updatedSeats[seatIndex].isButton = true;
              }

              // Ensure seat is occupied and linked to player
              updatedSeats[seatIndex].isOccupied = true;
              updatedSeats[seatIndex].player = player;
            }
          }
        });

        console.log('Updated seats:', updatedSeats);
        return updatedSeats;
      });

      // Set face-down cards for opponents who don't have cards yet; do not show cards for standing players
      const standingIds: string[] = standingIdsFromMessage.length > 0 ? standingIdsFromMessage : (standingPlayerIdsRef.current || []);
      const currentMainPlayerId = mainPlayerIdRef.current || mainPlayerId;
      const finalPlayers = updatedPlayers.map((p) => {
        if (standingIds.includes(p.uuid)) {
          return { ...p, hand: [] };
        }
        if (p.uuid !== currentMainPlayerId && (!p.hand || p.hand.length === 0)) {
          return {
            ...p,
            hand: [
              { rank: 0, suit: '?', isFaceUp: false },
              { rank: 0, suit: '?', isFaceUp: false },
            ],
          };
        }
        return p;
      });

      console.log('Updated players:', finalPlayers);
      return finalPlayers;
    });
  };

  const handleDealCards = (message: SocketMessage, userId?: string) => {
    if (!gameRef.current) return;

    console.log('handleDealCards called with:', message);
    const handString = message.hand as string;
    const messagePlayerId = message.playerId as string;

    // Use ref to get the latest mainPlayerId value, or use userId from callback if provided
    const currentMainPlayerId = userId || mainPlayerIdRef.current || mainPlayerId;
    console.log('handString:', handString, 'messagePlayerId:', messagePlayerId, 'mainPlayerId (state):', mainPlayerId, 'mainPlayerId (ref):', mainPlayerIdRef.current, 'userId (param):', userId, 'currentMainPlayerId:', currentMainPlayerId);

    if (handString && messagePlayerId === currentMainPlayerId) {
      // Parse hand string format: "9-c&5-s&" or "14-s&13-s" (rank-suit&rank-suit with optional trailing &)
      // Split by &, filter out empty strings, and parse each card
      const cardStrings = handString.split('&').filter(str => str && str.trim().length > 0);
      const cards = cardStrings.map(cardStr => {
        const parsed = parseCard(cardStr.trim());
        // Ensure cards are face-up for the main player
        return { ...parsed, isFaceUp: true };
      }).filter(card => card.rank > 0);

      console.log('Parsed main player cards:', cards);
      console.log('Card strings:', cardStrings);

      if (cards.length > 0) {
        gameRef.current.allocateCards(cards);

        const standingIds = standingPlayerIdsRef.current || [];
        setPlayers((prevPlayers) => {
          const updatedPlayers = prevPlayers.map((p) => {
            if (standingIds.includes(p.uuid)) {
              return { ...p, hand: [] };
            }
            if (p.isMainPlayer || p.uuid === currentMainPlayerId) {
              return {
                ...p,
                hand: cards.map(card => ({ ...card, isFaceUp: true })),
                isFolded: false, // New hand: clear folded state so cards are not greyed out
              };
            }
            if (!p.hand || p.hand.length === 0) {
              return {
                ...p,
                hand: [
                  { rank: 0, suit: '?', isFaceUp: false },
                  { rank: 0, suit: '?', isFaceUp: false },
                ],
              };
            }
            return p;
          });
          return updatedPlayers;
        });
      } else {
        console.error('No valid cards parsed from hand string:', handString);
      }
    } else {
      // For other players (when server sends DEAL_CARDS for opponents), set face-down cards; skip standing players
      const currentMainPlayerId = mainPlayerIdRef.current || mainPlayerId;
      const standingIds = standingPlayerIdsRef.current || [];
      setPlayers((prevPlayers) => {
        const updatedPlayers = prevPlayers.map((p) => {
          if (standingIds.includes(p.uuid)) {
            return { ...p, hand: [] };
          }
          if (p.uuid !== currentMainPlayerId && (!p.hand || p.hand.length === 0)) {
            return {
              ...p,
              hand: [
                { rank: 0, suit: '?', isFaceUp: false },
                { rank: 0, suit: '?', isFaceUp: false },
              ],
            };
          }
          return p;
        });
        return updatedPlayers;
      });
    }
  };

  // Timer countdown effect - runs for any player in action, not just main player
  useEffect(() => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Timer should count down for any player in action (currentPlayerIndex !== null)
    if (timer > 0 && currentPlayerIndex !== null) {
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };
    }
  }, [timer, currentPlayerIndex]);

  // Keep ref in sync for handlers that read standing player IDs
  useEffect(() => {
    standingPlayerIdsRef.current = Object.keys(standingSecondsByPlayer);
  }, [standingSecondsByPlayer]);

  useEffect(() => {
    isObserverRef.current = isObserver;
  }, [isObserver]);

  const hasSeatRef = useRef(false);
  useEffect(() => {
    hasSeatRef.current = players.some((p) => p.uuid === mainPlayerId);
  }, [players, mainPlayerId]);

  const hasShownObserverGlowRef = useRef(false);
  // When observer first joins (no seat), glow empty seats; clear after 5s or when they open join modal. Do not depend on players (new ref every update) to avoid infinite loop.
  useEffect(() => {
    if (!isObserver) {
      setShowObserverJoinGlow(false);
      hasShownObserverGlowRef.current = false;
      return;
    }
    if (hasSeatRef.current) {
      setShowObserverJoinGlow(false);
      return;
    }
    if (!hasShownObserverGlowRef.current) {
      hasShownObserverGlowRef.current = true;
      setShowObserverJoinGlow(true);
    }
    const t = setTimeout(() => setShowObserverJoinGlow(false), 5000);
    return () => clearTimeout(t);
  }, [isObserver, mainPlayerId]);
  useEffect(() => {
    if (joinSeatModal) setShowObserverJoinGlow(false);
  }, [joinSeatModal]);

  // Local standing countdown: tick down every second; server syncs every 20s via STANDING_COUNTDOWN
  useEffect(() => {
    const interval = setInterval(() => {
      setStandingSecondsByPlayer((prev) => {
        if (Object.keys(prev).length === 0) return prev;
        const next: Record<string, number> = {};
        Object.entries(prev).forEach(([id, sec]) => {
          const s = Math.max(0, sec - 1);
          if (s > 0) next[id] = s;
        });
        return Object.keys(next).length ? next : {};
      });
      setDisconnectGraceSecondsByPlayer((prev) => {
        if (Object.keys(prev).length === 0) return prev;
        const next: Record<string, number> = {};
        Object.entries(prev).forEach(([id, sec]) => {
          const s = Math.max(0, sec - 1);
          if (s > 0) next[id] = s;
        });
        return Object.keys(next).length ? next : {};
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Lobby data management removed - now in Lobby component

  const handleStartTimer = (message: SocketMessage) => {
    const playerIndex = message.playerInActionIndex as number;
    const allowedTime = (message.allowedTimeForPlayerToActServer as number) || 30;
    const highestBetValue = (message.highestBet as number) || 0;
    const firstBetAmountValue = (message.firstBetAmount as number) || 0;
    const playerLastRoundBetValue = (message.playerLastRoundBet as number) || 0;
    const noBetYetValue = message.noBetYet === 'true' || message.noBetYet === true;

    setCurrentPlayerIndex(playerIndex);
    currentPlayerIndexRef.current = playerIndex;
    setHighestBet(highestBetValue);
    // firstBetAmountValue used directly, no need to store in state
    setPlayerLastRoundBet(playerLastRoundBetValue);
    setNoBetYet(noBetYetValue);

    // Use functional update to get latest players and calculate raise amounts
    setPlayers((prevPlayers) => {
      const currentPlayer = prevPlayers.find((p) => p.seatIndex === playerIndex);
      const isMain = currentPlayer?.isMainPlayer || false;
      setIsMyTurn(isMain);
      if (isMain && !isMyTurnRef.current) {
        playTurnSound();
        isMyTurnRef.current = true;
      }
      if (!isMain) isMyTurnRef.current = false;

      if (currentPlayer?.isMainPlayer) {
        // Calculate minimum and maximum raise amounts (matching Java logic)
        const playerChips = currentPlayer.chips;
        const amountToCall = highestBetValue - playerLastRoundBetValue;
        const availablePlayerChipsWithRoundBet = playerChips + playerLastRoundBetValue;
        const minimumRaiseAmount = highestBetValue + firstBetAmountValue;

        // If player can't even call, they can only go all-in (Call button shows "All In", no third option)
        if (amountToCall >= playerChips) {
          setMinRaiseAmount(0);
          setMaxRaiseAmount(0);
          setRaiseAmount(0);
        } else if (minimumRaiseAmount >= availablePlayerChipsWithRoundBet) {
          // Can call but can't afford min raise: show Fold, Call, and All In (send all-in total when they click All In)
          setMinRaiseAmount(0);
          setMaxRaiseAmount(0);
          setRaiseAmount(availablePlayerChipsWithRoundBet);
        } else {
          // Normal case: can raise – after new deal reset to min; otherwise clamp so slider doesn't reset on mouseup
          setMinRaiseAmount(minimumRaiseAmount);
          setMaxRaiseAmount(availablePlayerChipsWithRoundBet);
          if (raiseAmountShouldResetToMinRef.current) {
            raiseAmountShouldResetToMinRef.current = false;
            setRaiseAmount(minimumRaiseAmount);
          } else {
            setRaiseAmount((prev) => Math.max(minimumRaiseAmount, Math.min(availablePlayerChipsWithRoundBet, prev || minimumRaiseAmount)));
          }
        }
      }

      return prevPlayers;
    });

    setTimer(allowedTime);
  };

  /** Sync current turn and server timer (for late joiners and timer drift). Uses server authority for timer. */
  const handleTurnAndTimerSync = (message: SocketMessage) => {
    const playerIndex = message.playerInActionIndex as number;
    const timeRemainingSeconds = (message.timeRemainingSeconds as number) ?? 0;
    const highestBetValue = (message.highestBet as number) ?? 0;
    const firstBetAmountValue = (message.firstBetAmount as number) ?? 0;
    const playerLastRoundBetValue = (message.playerLastRoundBet as number) ?? 0;
    const noBetYetValue = message.noBetYet === 'true' || message.noBetYet === true;

    setCurrentPlayerIndex(playerIndex);
    currentPlayerIndexRef.current = playerIndex;
    setHighestBet(highestBetValue);
    setPlayerLastRoundBet(playerLastRoundBetValue);
    setNoBetYet(noBetYetValue);
    setTimer(timeRemainingSeconds);

    setPlayers((prevPlayers) => {
      const currentPlayer = prevPlayers.find((p) => p.seatIndex === playerIndex);
      const isMain = currentPlayer?.isMainPlayer ?? false;
      setIsMyTurn(isMain);
      if (isMain && !isMyTurnRef.current) {
        playTurnSound();
        isMyTurnRef.current = true;
      }
      if (!isMain) isMyTurnRef.current = false;

      if (currentPlayer?.isMainPlayer) {
        const playerChips = currentPlayer.chips;
        const amountToCall = highestBetValue - playerLastRoundBetValue;
        const availablePlayerChipsWithRoundBet = playerChips + playerLastRoundBetValue;
        const minimumRaiseAmount = highestBetValue + firstBetAmountValue;

        if (amountToCall >= playerChips) {
          setMinRaiseAmount(0);
          setMaxRaiseAmount(0);
          setRaiseAmount(0);
        } else if (minimumRaiseAmount >= availablePlayerChipsWithRoundBet) {
          setMinRaiseAmount(0);
          setMaxRaiseAmount(0);
          setRaiseAmount(availablePlayerChipsWithRoundBet);
        } else {
          setMinRaiseAmount(minimumRaiseAmount);
          setMaxRaiseAmount(availablePlayerChipsWithRoundBet);
          if (raiseAmountShouldResetToMinRef.current) {
            raiseAmountShouldResetToMinRef.current = false;
            setRaiseAmount(minimumRaiseAmount);
          } else {
            setRaiseAmount((prev) => Math.max(minimumRaiseAmount, Math.min(availablePlayerChipsWithRoundBet, prev || minimumRaiseAmount)));
          }
        }
      }

      return prevPlayers;
    });
  };

  const handlePlayerFolded = (message: SocketMessage) => {
    console.log('handlePlayerFolded called with:', message);
    const playerId = message.playerId as string;

    // Stop timer if the folded player was the current player in action
    setPlayers((prevPlayers) => {
      const foldedPlayer = prevPlayers.find((p) => p.uuid === playerId);
      if (foldedPlayer && foldedPlayer.seatIndex === currentPlayerIndexRef.current) {
        stopTimer();
      }
      return prevPlayers;
    });

    // Update both players and seats together. Keep main player's hand so they can still see their cards (greyed out) and reveal them later.
    const isMainPlayer = playerId === mainPlayerIdRef.current || playerId === mainPlayerId;
    setPlayers((prevPlayers) => {
      const playerIndex = prevPlayers.findIndex((p) => p.uuid === playerId);
      if (playerIndex !== -1) {
        const updatedPlayers = [...prevPlayers];
        const player = updatedPlayers[playerIndex];
        updatedPlayers[playerIndex] = {
          ...player,
          isFolded: true,
          ...(isMainPlayer ? {} : { hand: undefined }),
        };

        // Update seat action text; never overwrite "Sitting Out"
        if (player.seatIndex !== undefined) {
          setSeats((prevSeats) => {
            const seatIndex = player.seatIndex!;
            if (prevSeats[seatIndex] && prevSeats[seatIndex].actionText !== 'Sitting Out') {
              const updatedSeats = [...prevSeats];
              updatedSeats[seatIndex] = {
                ...updatedSeats[seatIndex],
                actionText: 'Fold',
              };
              return updatedSeats;
            }
            return prevSeats;
          });
        }

        console.log('Updated players after fold:', updatedPlayers);
        return updatedPlayers;
      }
      return prevPlayers;
    });
  };

  const handlePlayerAction = (message: SocketMessage) => {
    console.log('handlePlayerAction called with:', message);
    const playerId = message.playerId as string;
    const playerActionText = message.playerActionText as string;
    const playerActionAmount = message.playerActionAmount as number;

    // Stop timer if the acting player was the current player in action
    setPlayers((prevPlayers) => {
      const actingPlayer = prevPlayers.find((p) => p.uuid === playerId);
      if (actingPlayer && actingPlayer.seatIndex === currentPlayerIndexRef.current) {
        stopTimer();
      }
      return prevPlayers;
    });

    // Update both players and seats together
    setPlayers((prevPlayers) => {
      const playerIndex = prevPlayers.findIndex((p) => p.uuid === playerId);
      if (playerIndex !== -1) {
        const updatedPlayers = [...prevPlayers];
        const player = updatedPlayers[playerIndex];
        updatedPlayers[playerIndex] = {
          ...player,
          chips: message.playerChipsRefreshed !== undefined ? message.playerChipsRefreshed : player.chips,
          currentBet: playerActionAmount !== undefined ? playerActionAmount : 0,
        };

        // Set action text on the seat; never overwrite "Sitting Out"
        if (player.seatIndex !== undefined) {
          setSeats((prevSeats) => {
            const seatIndex = player.seatIndex!;
            if (prevSeats[seatIndex] && prevSeats[seatIndex].actionText !== 'Sitting Out') {
              const updatedSeats = [...prevSeats];
              let actionText = playerActionText || 'Acted';
              if (playerActionText && playerActionText !== 'Check' && playerActionAmount !== undefined && playerActionAmount > 0) {
                actionText = `${playerActionText} $${playerActionAmount}`;
              }
              updatedSeats[seatIndex] = {
                ...updatedSeats[seatIndex],
                actionText: actionText,
              };
              return updatedSeats;
            }
            return prevSeats;
          });
        }

        return updatedPlayers;
      }
      return prevPlayers;
    });
  };

  const handleDealTableCards = (message: SocketMessage) => {
    console.log('handleDealTableCards called with:', message);

    // Stop timer when dealing table cards (especially river - final card means showdown)
    const cardsPositions = message.cardsPositions as string;
    if (cardsPositions === 'river') {
      stopTimer(); // River means showdown, no more actions
    }

    // Server sends cards as array of strings like ["13d", "2h", "8h"]
    // Also supports cardsPositions: "flop", "turn", "river"
    const cardsArray = message.cards || message.tableCards || [];

    if (Array.isArray(cardsArray) && cardsArray.length > 0) {
      const cards = cardsArray.map((cardStr: string) => parseCard(cardStr));
      console.log('Parsed table cards:', cards);

      // Handle cardsPositions to determine where to place cards
      // For flop: positions 0, 1, 2
      // For turn: position 3
      // For river: position 4
      const cardsPositions = message.cardsPositions as string;
      let startIndex = 0;

      if (cardsPositions === 'turn') {
        startIndex = 3;
      } else if (cardsPositions === 'river') {
        startIndex = 4;
      }

      // Update table cards - append or replace based on position
      setTableCards((prevCards) => {
        const updatedCards = [...prevCards];
        cards.forEach((card: Card, index: number) => {
          updatedCards[startIndex + index] = card;
        });
        return updatedCards.filter(card => card !== undefined);
      });

      // After the flop is dealt, hide small blind and big blind indicators; never clear "Sitting Out"
      if (cardsPositions === 'flop') {
        setSeats((prevSeats) =>
          prevSeats.map((s) => ({
            ...s,
            isSmallBlind: false,
            isBigBlind: false,
            actionText: s.actionText === 'Sitting Out' ? 'Sitting Out' : (s.actionText && (s.isSmallBlind || s.isBigBlind) ? undefined : s.actionText),
          }))
        );
      }

      if (gameRef.current) {
        gameRef.current.dealTableCards(cards);
      }
    }
  };

  const handleRevealTableCards = (message: SocketMessage) => {
    const cardsArray = message.cards || [];
    if (Array.isArray(cardsArray) && cardsArray.length > 0) {
      const cards = cardsArray.map((cardStr: string) => parseCard(cardStr));
      setTableCards(cards);
    }
  };

  const handleRefreshPot = (message: SocketMessage) => {
    console.log('handleRefreshPot called with:', message);

    // Stop timer - new round is starting
    stopTimer();

    // Extract pot from potText if available (format: "Pot:  $20")
    let potValue = message.pot || 0;
    if (message.potText) {
      const match = message.potText.match(/\$(\d+)/);
      if (match) {
        potValue = parseInt(match[1]);
      }
    }
    setPot(potValue);

    // Reset player actions - use functional update
    setPlayers((prevPlayers) => {
      const updatedPlayers = prevPlayers.map((p) => ({
        ...p,
        currentBet: 0,
      }));
      console.log('Updated players after refresh pot:', updatedPlayers);
      return updatedPlayers;
    });

    // Clear action texts except "Sitting Out" and "Fold" (keep until new hand is dealt)
    setSeats((prevSeats) => {
      return prevSeats.map((seat) => ({
        ...seat,
        actionText: (seat.actionText === 'Sitting Out' || seat.actionText === 'Fold') ? seat.actionText : undefined,
      }));
    });
  };

  const handleCelebrateAndPayWinner = (message: SocketMessage) => {
    const now = Date.now();
    if (now - lastCelebrationAtRef.current < CELEBRATION_DEBOUNCE_MS) {
      // Coalesce rapid celebration messages (e.g. multiple winners) to avoid re-render storm and crash
      return;
    }
    lastCelebrationAtRef.current = now;

    // Clear any previous celebration timeout to avoid overlapping updates and memory leaks
    if (celebrationTimeoutRef.current) {
      clearTimeout(celebrationTimeoutRef.current);
      celebrationTimeoutRef.current = null;
    }

    const playerId = typeof message.playerId === 'string' ? message.playerId : '';
    const playerNewBalance = typeof message.playerNewBalance === 'number' ? message.playerNewBalance : 0;
    const winMessageText = typeof message.winMessage === 'string' ? message.winMessage : '';
    const showBestCardsEffect = message.showBestCardsEffect === 'true' || message.showBestCardsEffect === true;
    const playerBestHand = message.playerBestHand as string | undefined;

    // Parse playerBestHand string (format: "[11s, 10c, 13h, 10h, 11h]")
    let bestHandCards: string[] = [];
    if (playerBestHand && typeof playerBestHand === 'string' && showBestCardsEffect) {
      try {
        const cardsString = playerBestHand.replace(/[[\]]/g, '').trim();
        bestHandCards = cardsString.split(',').map((c: string) => c.trim()).filter((c: string) => c.length >= 2);
      } catch {
        bestHandCards = [];
      }
    }

    setWinMessage(winMessageText || null);
    setWinningPlayerId(playerId || null);
    setWinningCards(bestHandCards);

    if (playerId) {
      setPlayers((prevPlayers) =>
        prevPlayers.map((p) =>
          p.uuid === playerId ? { ...p, chips: playerNewBalance } : p
        )
      );
    }

    // Auto-hide win message after 5 seconds; use ref so we can clear on unmount or next celebration
    celebrationTimeoutRef.current = setTimeout(() => {
      celebrationTimeoutRef.current = null;
      setWinMessage(null);
      setWinningPlayerId(null);
      setWinningCards([]);
    }, 5000);
  };

  const handlePayPlayerRoundBetDifference = (message: SocketMessage) => {
    console.log('handlePayPlayerRoundBetDifference called with:', message);
    const playerId = message.playerId as string;
    const playerNewBalance = message.playerNewBalance as number;

    // Update player's chips - use functional update
    setPlayers((prevPlayers) => {
      const updatedPlayers = prevPlayers.map((p) => {
        if (p.uuid === playerId) {
          return { ...p, chips: playerNewBalance, currentBet: 0 };
        }
        return p;
      });
      console.log('Updated players after pay difference:', updatedPlayers);
      return updatedPlayers;
    });
  };

  const handleShowAllCards = (message: SocketMessage) => {
    stopTimer();

    if (!message.playersCards) return;

    let playersCardsData: Array<{ playerId: string; playerCards: string }> = [];
    const maxPlayers = 9;

    if (Array.isArray(message.playersCards)) {
      playersCardsData = message.playersCards
        .slice(0, maxPlayers)
        .map((item: any) => ({
          playerId: String(item.playerId ?? item.player_id ?? '').trim(),
          playerCards: String(item.playerCards ?? item.player_cards ?? '').trim(),
        }))
        .filter((item) => item.playerId && item.playerCards);
    } else if (typeof message.playersCards === 'string') {
      try {
        const parsed = JSON.parse(message.playersCards);
        if (Array.isArray(parsed)) {
          playersCardsData = parsed
            .slice(0, maxPlayers)
            .map((item: any) => ({
              playerId: String(item.playerId ?? item.player_id ?? '').trim(),
              playerCards: String(item.playerCards ?? item.player_cards ?? '').trim(),
            }))
            .filter((item) => item.playerId && item.playerCards);
        }
      } catch {
        const javaString = message.playersCards.trim();
        if (javaString.startsWith('[') && javaString.endsWith(']')) {
          const content = javaString.slice(1, -1);
          const entries = content.split('}, {').map((e: string) => e.replace(/[{}]/g, ''));
          entries.forEach((entry: string) => {
            const playerIdMatch = entry.match(/playerId=([^,}]+)/);
            const playerCardsMatch = entry.match(/playerCards=([^,}]+)/);
            if (playerIdMatch?.[1] && playerCardsMatch?.[1] && playersCardsData.length < maxPlayers) {
              playersCardsData.push({
                playerId: playerIdMatch[1].trim(),
                playerCards: playerCardsMatch[1].trim(),
              });
            }
          });
        }
      }
    } else if (typeof message.playersCards === 'object' && message.playersCards !== null && !Array.isArray(message.playersCards)) {
      playersCardsData = Object.entries(message.playersCards)
        .slice(0, maxPlayers)
        .map(([pid, pcards]) => ({
          playerId: String(pid).trim(),
          playerCards: typeof pcards === 'string' ? pcards.trim() : JSON.stringify(pcards),
        }))
        .filter((item) => item.playerId && item.playerCards);
    }

    if (playersCardsData.length === 0) return;

    setPlayers((prevPlayers) => {
      const updatedPlayers = [...prevPlayers];
      for (const { playerId, playerCards } of playersCardsData) {
        const playerIndex = updatedPlayers.findIndex((p) => p.uuid === playerId);
        if (playerIndex === -1) continue;
        const cardStrings = playerCards.split('&').filter((s) => s && s.trim().length >= 2);
        const cards = cardStrings
          .slice(0, 7)
          .map((cardStr) => {
            const parsed = parseCard(cardStr.trim());
            return parsed.rank > 0 ? { ...parsed, isFaceUp: true } : null;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null);
        updatedPlayers[playerIndex] = { ...updatedPlayers[playerIndex], hand: cards };
      }
      return updatedPlayers;
    });
    setRevealedCardsByPlayer((prev) => {
      const next = { ...prev };
      for (const { playerId, playerCards } of playersCardsData) {
        const cardStrings = playerCards.split('&').filter((s) => s && s.trim().length >= 2);
        const cards = cardStrings
          .slice(0, 2)
          .map((cardStr) => {
            const parsed = parseCard(cardStr.trim());
            return parsed.rank > 0 ? { ...parsed, isFaceUp: true } : null;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null) as (Card | null)[];
        const arr: (Card | null)[] = [cards[0] ?? null, cards[1] ?? null];
        next[playerId] = arr;
      }
      return next;
    });
    setShowdownCardsShownThisHand(true);
  };

  const handleCardRevealed = (message: SocketMessage) => {
    const playerId = message.playerId as string;
    const cardIndex = message.cardIndex as number;
    const cardStr = message.card as string;
    if (!playerId || cardIndex == null || !cardStr) return;
    const card = parseCard(cardStr);
    if (card.rank === 0 && card.suit === '?') return;
    setRevealedCardsByPlayer((prev) => {
      const arr = [...(prev[playerId] ?? [null, null])];
      if (cardIndex >= 0 && cardIndex < arr.length) arr[cardIndex] = { ...card, isFaceUp: true };
      return { ...prev, [playerId]: arr };
    });
    setUserRevealedCardsByPlayer((prev) => {
      const arr = [...(prev[playerId] ?? [null, null])];
      if (cardIndex >= 0 && cardIndex < arr.length) arr[cardIndex] = { ...card, isFaceUp: true };
      return { ...prev, [playerId]: arr };
    });
    setPlayers((prev) => {
      const idx = prev.findIndex((p) => p.uuid === playerId);
      if (idx === -1) return prev;
      const p = prev[idx];
      // Ensure hand has 2 slots so we can set the revealed card even when player had folded (hand was undefined)
      const hand: (Card | null)[] = [null, null];
      if (p.hand?.length) {
        p.hand.forEach((c, i) => { if (i < 2) hand[i] = c; });
      }
      if (cardIndex >= 0 && cardIndex < 2) hand[cardIndex] = { ...card, isFaceUp: true };
      const next = [...prev];
      next[idx] = { ...p, hand: hand.filter((c): c is Card => c != null) };
      return next;
    });
  };

  const handleStartGameJoinedPlayer = (message: SocketMessage, userId: string, username: string) => {
    console.log('handleStartGameJoinedPlayer called with:', message);
    const asObserver = message.isObserver === true;
    if (asObserver) {
      isObserverRef.current = true;
      setIsObserver(true);
    }

    // Get roomId from message
    const roomIdFromMessage = message.roomId as string;
    if (roomIdFromMessage) {
      setRoomId(roomIdFromMessage);
      // Store roomId in localStorage so we can reconnect on page refresh
      localStorage.setItem('currentRoomId', roomIdFromMessage);
    }

    // Get game config from message
    const minEntryChips = message.minEntryChips as number || 1000;
    const maxEntryChips = message.maxEntryChips as number || 10000;
    const allowedNumberOfCardsPerHand = message.allowedNumberOfCardsPerHand as number || 2;
    setTableMinChips(minEntryChips);
    setTableMaxChips(maxEntryChips);

    // Initialize game (blinds will be set from game state update)
    const config = {
      smallBlind: 0, // Will be updated from game state
      bigBlind: 0, // Will be updated from game state
      numberOfPlayers: 9,
      minEntryChips,
      maxEntryChips,
      allowedNumberOfCardsPerHand,
    };

    const game = new TexasHoldemGame(config, message.gameId || '');
    const mainPlayerIdFromMessage = message.playerId as string;
    game.setMainInfo(mainPlayerIdFromMessage || userId, username, roomIdFromMessage);

    // Create players from message.playersInfo
    let playersReady: PlayerReadyForSeating[] = [];

    if (message.playersInfo) {
      const playersInfo = message.playersInfo as Record<string, { name: string; seatIndex: number; chips?: number }>;
      playersReady = Object.entries(playersInfo).map(([playerId, info]) => ({
        playerId: playerId,
        playerName: info.name,
        seatIndex: info.seatIndex,
        chips: info.chips || minEntryChips,
      }));
    }

    console.log('Initializing game for joined player with players:', playersReady);

    if (playersReady.length > 0) {
      game.initiateGame(playersReady);

      const gameState = game.getGameState();
      console.log('Game state initialized for joined player:', gameState);

      // Store game in ref
      gameRef.current = game;

      // Set all game state and clear lobby
      setGameState(gameState);
      setPlayers(gameState.players);
      setSeats(gameState.seats);

      // Update main player ID
      if (mainPlayerIdFromMessage) {
        setMainPlayerId(mainPlayerIdFromMessage);
        mainPlayerIdRef.current = mainPlayerIdFromMessage;
      }

      // Only one player seated: no hand in progress, no cards, no timer
      if (playersReady.length === 1) {
        setNotEnoughPlayersMessage('Waiting for more players to join');
        notEnoughPlayersRef.current = true;
        stopTimer();
        setCurrentPlayerIndex(null);
        currentPlayerIndexRef.current = null;
        setTimer(0);
        setTableCards([]);
        setPlayers((prev) => prev.map((p) => ({ ...p, hand: [] })));
        setPot(0);
        setHighestBet(0);
        return;
      }

      // Update game state from JSON (similar to Java's updateGameStateFromJson)
      // Update pot, highest bet, table cards, positions, etc.
      if (message.pot !== undefined) {
        setPot(message.pot as number);
      }
      if (message.highestBet !== undefined) {
        setHighestBet(message.highestBet as number);
      }
      // Table cards: use only what server sent; cap by game state so we never show future streets
      if (message.tableCards && Array.isArray(message.tableCards)) {
        const maxByState: Record<string, number> = {
          GAME_PRE_FLOP: 0,
          GAME_FLOP: 3,
          GAME_TURN: 4,
          GAME_RIVER: 5,
          GAME_RESULT: 5,
        };
        const gameStateStr = (message.gameState as string) || '';
        const maxCards = maxByState[gameStateStr] ?? 5;
        const raw = (message.tableCards as string[]).map(cardStr => parseCard(cardStr));
        const tableCards = raw.slice(0, Math.min(maxCards, raw.length));
        setTableCards(tableCards);
      }
      if (message.buttonIndex !== undefined) {
        setSeats((prevSeats) => {
          const updatedSeats = prevSeats.map((s, idx) => ({
            ...s,
            isButton: idx === (message.buttonIndex as number),
          }));
          return updatedSeats;
        });
      }
      if (message.smallBlindIndex !== undefined && message.bigBlindIndex !== undefined) {
        const gameStateStr = (message.gameState as string) || '';
        const isPreflop = gameStateStr === 'GAME_PRE_FLOP';
        setSeats((prevSeats) => {
          const updatedSeats = prevSeats.map((s, idx) => ({
            ...s,
            isSmallBlind: isPreflop && idx === (message.smallBlindIndex as number),
            isBigBlind: isPreflop && idx === (message.bigBlindIndex as number),
          }));
          return updatedSeats;
        });
      }

      // Seat action text and standing from playersInfo; never overwrite "Sitting Out"
      if (message.playersInfo && typeof message.playersInfo === 'object') {
        const playersInfo = message.playersInfo as Record<string, { seatIndex?: number; lastAction?: string; lastActionAmount?: number; standing?: boolean; standingSecondsRemaining?: number; onExitStandby?: boolean }>;
        setSeats((prevSeats) => {
          const updatedSeats = prevSeats.map((s) => ({ ...s }));
          const standingMap: Record<string, number> = {};
          Object.entries(playersInfo).forEach(([pid, info]) => {
            const idx = info.seatIndex;
            if (idx !== undefined && updatedSeats[idx]) {
              if (info.onExitStandby) {
                updatedSeats[idx] = { ...updatedSeats[idx], actionText: 'Sitting Out' };
              }
              if (info.lastAction || info.lastActionAmount) {
                const keepSittingOut = updatedSeats[idx].actionText === 'Sitting Out' || info.onExitStandby;
                if (!keepSittingOut) {
                  const text = info.lastActionAmount ? `${info.lastAction} $${info.lastActionAmount}` : (info.lastAction || '');
                  updatedSeats[idx] = { ...updatedSeats[idx], actionText: text || undefined };
                }
              }
              if (info.standing !== undefined) {
                updatedSeats[idx] = { ...updatedSeats[idx], isStanding: info.standing, standingSecondsRemaining: info.standingSecondsRemaining ?? 0 };
                if (info.standing && info.standingSecondsRemaining != null) standingMap[pid] = info.standingSecondsRemaining;
              }
            }
          });
          setStandingSecondsByPlayer((prev) => ({ ...prev, ...standingMap }));
          return updatedSeats;
        });
      }

      // Main player's own hand (late joiner in the hand); observers have no hand
      if (!asObserver && message.mainPlayerHand && typeof message.mainPlayerHand === 'string') {
        const cardStrings = (message.mainPlayerHand as string).split('&').filter((s) => s && s.trim().length >= 2);
        const cards = cardStrings
          .slice(0, 7)
          .map((cardStr) => {
            const parsed = parseCard(cardStr.trim());
            return parsed.rank > 0 ? { ...parsed, isFaceUp: true } : null;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null);
        if (cards.length > 0) {
          setPlayers((prevPlayers) =>
            prevPlayers.map((p) =>
              p.uuid === mainPlayerIdFromMessage || p.isMainPlayer
                ? { ...p, hand: cards }
                : p
            )
          );
        }
      }

      // All players' cards when face-up (e.g. after showdown / celebration)
      if (message.playersCards && Array.isArray(message.playersCards) && message.playersCards.length > 0) {
        const playersCardsData = message.playersCards
          .slice(0, 9)
          .map((item: any) => ({
            playerId: String(item.playerId ?? item.player_id ?? '').trim(),
            playerCards: String(item.playerCards ?? item.player_cards ?? '').trim(),
          }))
          .filter((item) => item.playerId && item.playerCards);
        setPlayers((prevPlayers) => {
          const updatedPlayers = [...prevPlayers];
          playersCardsData.forEach(({ playerId, playerCards }) => {
            const i = updatedPlayers.findIndex((p) => p.uuid === playerId);
            if (i === -1) return;
            const cardStrings = playerCards.split('&').filter((s) => s && s.trim().length >= 2);
            const cards = cardStrings
              .slice(0, 7)
              .map((cardStr) => {
                const parsed = parseCard(cardStr.trim());
                return parsed.rank > 0 ? { ...parsed, isFaceUp: true } : null;
              })
              .filter((c): c is NonNullable<typeof c> => c !== null);
            updatedPlayers[i] = { ...updatedPlayers[i], hand: cards };
          });
          return updatedPlayers;
        });
      }

      // Current turn and timer (so late joiner sees whose turn and countdown; action bar if their turn)
      const playerInActionIndex = message.playerInActionIndex as number | undefined;
      const timeRemaining = message.timeRemaining as number | undefined;
      if (playerInActionIndex !== undefined && playerInActionIndex >= 0) {
        setCurrentPlayerIndex(playerInActionIndex);
        currentPlayerIndexRef.current = playerInActionIndex;
      }
      if (timeRemaining !== undefined) {
        setTimer(timeRemaining);
      }
      const firstBetAmountValue = (message.firstBetAmount as number) ?? 0;
      const playerLastRoundBetValue = (message.playerLastRoundBet as number) ?? 0;
      const noBetYetValue = message.noBetYet === 'true' || message.noBetYet === true;
      if (message.isPlayerActing && message.highestBet !== undefined) {
        setHighestBet(message.highestBet as number);
        setPlayerLastRoundBet(playerLastRoundBetValue);
        setNoBetYet(noBetYetValue);
        setPlayers((prevPlayers) => {
          const currentPlayer = prevPlayers.find((p) => p.seatIndex === playerInActionIndex);
          const isMain = currentPlayer?.isMainPlayer ?? false;
          setIsMyTurn(isMain);
          if (isMain && !isMyTurnRef.current) {
            playTurnSound();
            isMyTurnRef.current = true;
          }
          if (!isMain) isMyTurnRef.current = false;
          if (currentPlayer?.isMainPlayer && message.highestBet !== undefined) {
            const highestBetValue = message.highestBet as number;
            const playerChips = currentPlayer.chips;
            const amountToCall = highestBetValue - playerLastRoundBetValue;
            const availablePlayerChipsWithRoundBet = playerChips + playerLastRoundBetValue;
            const minimumRaiseAmount = highestBetValue + firstBetAmountValue;
            if (amountToCall >= playerChips) {
              setMinRaiseAmount(0);
              setMaxRaiseAmount(0);
              setRaiseAmount(0);
            } else if (minimumRaiseAmount >= availablePlayerChipsWithRoundBet) {
              setMinRaiseAmount(0);
              setMaxRaiseAmount(0);
              setRaiseAmount(availablePlayerChipsWithRoundBet);
            } else {
              setMinRaiseAmount(minimumRaiseAmount);
              setMaxRaiseAmount(availablePlayerChipsWithRoundBet);
              if (raiseAmountShouldResetToMinRef.current) {
                raiseAmountShouldResetToMinRef.current = false;
                setRaiseAmount(minimumRaiseAmount);
              } else {
                setRaiseAmount((prev) => Math.max(minimumRaiseAmount, Math.min(availablePlayerChipsWithRoundBet, prev || minimumRaiseAmount)));
              }
            }
          }
          return prevPlayers;
        });
      }

      // Placeholder face-down cards only for opponents who are IN the current hand but we don't have their cards yet.
      // Players not in the hand (inGame: false, standing, on standby) get no cards.
      const standingIdsFromPlayersInfo: string[] = message.playersInfo && typeof message.playersInfo === 'object'
        ? Object.entries(message.playersInfo as Record<string, { standing?: boolean }>)
            .filter(([, info]) => info?.standing)
            .map(([id]) => id)
        : (standingPlayerIdsRef.current || []);
      const notInHandIds: string[] = message.playersInfo && typeof message.playersInfo === 'object'
        ? Object.entries(message.playersInfo as Record<string, { inGame?: boolean }>)
            .filter(([, info]) => info?.inGame === false)
            .map(([id]) => id)
        : [];
      const noCardsPlayerIds = new Set([...standingIdsFromPlayersInfo, ...notInHandIds]);
      setPlayers((prevPlayers) =>
        prevPlayers.map((p) => {
          if (noCardsPlayerIds.has(p.uuid)) {
            return { ...p, hand: [] };
          }
          if (p.uuid !== mainPlayerIdFromMessage && (!p.hand || p.hand.length === 0)) {
            return {
              ...p,
              hand: [
                { rank: 0, suit: '?', isFaceUp: false },
                { rank: 0, suit: '?', isFaceUp: false },
              ],
            };
          }
          return p;
        })
      );

      // Game started - player should see game table
    } else {
      console.warn('No players available to initialize game for joined player');
    }
  };

  const handleAddPlayerToGame = (message: SocketMessage) => {
    console.log('handleAddPlayerToGame called with:', message);
    const playerId = message.playerId as string;
    const playerName = message.playerName as string;
    const seatIndex = message.playerSeat as number;
    const playerChips = message.playerChips as number;
    const isMe = playerId === mainPlayerIdRef.current;
    if (isMe) {
      isObserverRef.current = false;
      setIsObserver(false);
      setWinMessage(null);
    }

    // Add player to the game and update seat together
    setPlayers((prevPlayers) => {
      const existingPlayerIndex = prevPlayers.findIndex((p) => p.uuid === playerId);
      let newPlayer: Player;

      if (existingPlayerIndex !== -1) {
        const updatedPlayers = [...prevPlayers];
        updatedPlayers[existingPlayerIndex] = {
          ...updatedPlayers[existingPlayerIndex],
          chips: playerChips,
          seatIndex: seatIndex,
          isMainPlayer: isMe,
        };
        newPlayer = updatedPlayers[existingPlayerIndex];
        return updatedPlayers;
      } else {
        newPlayer = {
          uuid: playerId,
          name: playerName,
          chips: playerChips,
          isMainPlayer: isMe,
          seatIndex: seatIndex,
          hand: [],
        };
        return [...prevPlayers, newPlayer];
      }
    });

    // Update seat with the new player
    setSeats((prevSeats) => {
      const updatedSeats = [...prevSeats];
      if (updatedSeats[seatIndex]) {
        updatedSeats[seatIndex] = {
          ...updatedSeats[seatIndex],
          isOccupied: true,
          player: {
            uuid: playerId,
            name: playerName,
            chips: playerChips,
            isMainPlayer: isMe,
            seatIndex: seatIndex,
          },
        };
      }
      return updatedSeats;
    });
  };

  const handleGameNotEnoughPlayers = (message: SocketMessage) => {
    console.log('Game not enough players:', message);

    stopTimer();
    setIsMyTurn(false);
    isMyTurnRef.current = false;
    setCurrentPlayerIndex(null);
    currentPlayerIndexRef.current = null;

    setTableCards([]);
    setPlayers((prev) => prev.map((p) => ({ ...p, hand: [] })));
    setRevealedCardsByPlayer({});
    setUserRevealedCardsByPlayer({});
    setShowdownCardsShownThisHand(false);
    setShowRevealTableCardsOption(false);
    setWinMessage(null);

    setSeats((prevSeats) =>
      prevSeats.map((s) => ({
        ...s,
        isButton: false,
        isSmallBlind: false,
        isBigBlind: false,
        actionText: s.actionText === 'Sitting Out' || s.actionText === 'Fold' ? s.actionText : undefined,
      }))
    );

    setNotEnoughPlayersMessage('Waiting for more players to join');
    notEnoughPlayersRef.current = true;

    // Clear any ghost/bot seats: only keep seats occupied if that player is still in our players list
    setPlayers((prevPlayers) => {
      setSeats((prevSeats) =>
        prevSeats.map((seat) => {
          const p = prevPlayers.find((pp) => pp.seatIndex === seat.seatIndex);
          return { ...seat, isOccupied: !!p, player: p };
        })
      );
      return prevPlayers;
    });
  };

  const handleGameEnded = (message: SocketMessage) => {
    console.log('Game ended');
    stopTimer(); // Stop timer when game ends
    navigate('/');
  };

  const handlePlayerSitOut = (message: SocketMessage) => {
    console.log('handlePlayerSitOut called with:', message);
    const playerId = message.playerId as string;
    setDisconnectGraceSecondsByPlayer((prev) => {
      const next = { ...prev };
      delete next[playerId];
      return next;
    });
    const isMe = playerId === mainPlayerIdRef.current || playerId === mainPlayerId || playerId === (typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null);
    const reason = message.reason as string | undefined;
    const seatIndexFromMsg = typeof message.seatIndex === 'number' ? message.seatIndex : undefined;

    if (reason === 'inactivity') {
      if (isMe) {
        isObserverRef.current = true;
        setIsObserver(true);
        setWinMessage("You were sat out for not being active in the game.");
        if (bustedMessageTimeoutRef.current) clearTimeout(bustedMessageTimeoutRef.current);
        bustedMessageTimeoutRef.current = setTimeout(() => {
          setWinMessage(null);
          bustedMessageTimeoutRef.current = null;
        }, 8000);
        stopTimer();
        setCurrentPlayerIndex(null);
        currentPlayerIndexRef.current = null;
        setPlayers((prev) => prev.filter((p) => p.uuid !== playerId));
        setSeats((prev) => {
          const seatIdx = seatIndexFromMsg ?? prev.find((s) => s.player?.uuid === playerId)?.seatIndex;
          if (typeof seatIdx === 'number' && seatIdx >= 0) {
            return prev.map((s) => (s.seatIndex === seatIdx ? { ...s, isOccupied: false, player: undefined } : s));
          }
          return prev;
        });
        setObservers((prev) => {
          if (prev.some((o) => o.playerId === playerId)) return prev;
          return [...prev, { playerId, playerName: (typeof localStorage !== 'undefined' ? localStorage.getItem('username') : null) || 'You' }];
        });
        return;
      }
      setObservers((prev) => {
        if (prev.some((o) => o.playerId === playerId)) return prev;
        return [...prev, { playerId, playerName: String(message.playerName || 'Player') }];
      });
      setPlayers((prev) => prev.filter((p) => p.uuid !== playerId));
      setSeats((prev) => {
        const seatIdx = seatIndexFromMsg ?? prev.find((s) => s.player?.uuid === playerId)?.seatIndex;
        if (typeof seatIdx === 'number' && seatIdx >= 0) {
          return prev.map((s) => (s.seatIndex === seatIdx ? { ...s, isOccupied: false, player: undefined } : s));
        }
        return prev;
      });
      return;
    }

    setPlayers((prevPlayers) => {
      const playerIndex = prevPlayers.findIndex((p) => p.uuid === playerId);
      if (playerIndex !== -1) {
        const updatedPlayers = [...prevPlayers];
        const player = updatedPlayers[playerIndex];
        updatedPlayers[playerIndex] = { ...player, hand: [] };

        setSeats((prevSeats) =>
          prevSeats.map((seat) =>
            seat.seatIndex === player.seatIndex
              ? { ...seat, actionText: 'Sitting Out' }
              : seat
          )
        );

        return updatedPlayers;
      }
      return prevPlayers;
    });

    if (isMe) {
      if (lobbyWindowRef.current && !lobbyWindowRef.current.closed) {
        lobbyWindowRef.current.close();
        lobbyWindowRef.current = null;
      }
      localStorage.removeItem('currentRoomId');
      navigate('/dashboard');
    }
  };

  const handlePlayerExited = (message: SocketMessage) => {
    console.log('handlePlayerExited called with:', message);
    setPlayers((prevPlayers) => {
      const player = prevPlayers.find((p) => p.uuid === message.playerId);
      if (player) {
        setSeats((prevSeats) => {
          const seatIndex = player.seatIndex || 0;
          const updatedSeats = [...prevSeats];
          if (updatedSeats[seatIndex]) {
            updatedSeats[seatIndex] = {
              ...updatedSeats[seatIndex],
              isOccupied: false,
              player: undefined,
            };
          }
          return updatedSeats;
        });
        return prevPlayers.filter((p) => p.uuid !== message.playerId);
      }
      return prevPlayers;
    });
  };

  const sendPlayerAction = useCallback((action: PlayerHandActions, amount?: number) => {
    if (!socketServiceRef.current || !roomId) return;

    const message: any = {
      type: action === PlayerHandActions.FOLD
        ? UserMessages.PLAYER_FOLDED
        : action === PlayerHandActions.CALL || action === PlayerHandActions.CHECK
          ? UserMessages.PLAYER_CALLED
          : UserMessages.PLAYER_RAISED,
      playerId: mainPlayerId,
      roomId: roomId,
    };

    if (amount !== undefined) {
      message.playerActionAmount = amount;
    }

    socketServiceRef.current.sendMessage(message);
    setIsMyTurn(false);
    isMyTurnRef.current = false;
  }, [roomId, mainPlayerId]);

  const sendRevealCard = useCallback((cardIndex: number) => {
    if (!socketServiceRef.current || !roomId || !mainPlayerId) return;
    socketServiceRef.current.sendMessage({
      type: UserMessages.REVEAL_CARD,
      roomId,
      playerId: mainPlayerId,
      cardIndex,
    });
  }, [roomId, mainPlayerId]);

  const sendRevealRemainingTableCards = useCallback(() => {
    if (!socketServiceRef.current || !roomId) return;
    socketServiceRef.current.sendMessage({
      type: UserMessages.REVEAL_REMAINING_TABLE_CARDS,
      roomId,
    });
  }, [roomId]);

  const sendJoinAsPlayer = useCallback((seatIndex: number, chips: number) => {
    if (!socketServiceRef.current || !roomId || !mainPlayerId) return;
    const clampedChips = Math.max(tableMinChips, Math.min(tableMaxChips, chips));
    socketServiceRef.current.sendMessage({
      type: UserMessages.JOIN_AS_PLAYER,
      roomId,
      playerId: mainPlayerId,
      seatIndex,
      chips: clampedChips,
    });
  }, [roomId, mainPlayerId, tableMinChips, tableMaxChips]);

  /** Display cards for a seat: use revealed card if any, else hand card. For other players we only show revealed cards (no hand leak). */
  const getDisplayCardsForPlayer = (p: Player): (Card | null)[] => {
    const revealed = revealedCardsByPlayer[p.uuid];
    const hand = p.hand ?? [];
    return [0, 1].map((i) => {
      if (revealed?.[i]) return revealed[i];
      if (p.uuid === mainPlayerId) return hand[i] ?? null;
      return null;
    });
  };

  const handleFold = () => {
    stopTimer(); // Stop timer when player acts
    sendPlayerAction(PlayerHandActions.FOLD);
  };

  const handleCall = () => {
    stopTimer(); // Stop timer when player acts
    sendPlayerAction(PlayerHandActions.CALL);
  };

  const handleRaise = () => {
    stopTimer(); // Stop timer when player acts
    // When "can't raise, only all-in" we set raiseAmount to all-in total; fallback if state not yet updated
    const mainPlayer = players.find((p) => p.isMainPlayer);
    const chips = mainPlayer?.chips ?? 0;
    const amount = raiseAmount > 0 ? raiseAmount : (playerLastRoundBet + chips);
    sendPlayerAction(PlayerHandActions.RAISE, amount);
  };

  // Exit button removed - window close detection handles exiting

  // Chat, lobby, and room management handlers removed - now in Lobby component

  const closeErrorModal = () => {
    const shouldNavigate = errorModal?.navigateToDashboardOnClose;
    setErrorModal(null);
    if (shouldNavigate) {
      localStorage.removeItem('currentRoomId');
      navigate('/dashboard');
    }
  };

  // If no game state, show loading message (and error modal if room not found etc.)
  if (!gameState) {
    return (
      <div className="game-container">
        {errorModal && (
          <div className="game-error-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="game-error-modal-title">
            <div className="game-error-modal">
              <div className="game-error-modal-header">
                <h2 id="game-error-modal-title" className="game-error-modal-title">Message</h2>
                <button type="button" className="game-error-modal-close" onClick={closeErrorModal} aria-label="Close">×</button>
              </div>
              <p className="game-error-modal-message">{errorModal.message}</p>
              <div className="game-error-modal-actions">
                <button type="button" className="game-error-modal-btn" onClick={closeErrorModal}>OK</button>
              </div>
            </div>
          </div>
        )}
        <div className="game-loading">
          <h2>Waiting for game to start...</h2>
          <p>Connecting to game...</p>
        </div>
      </div>
    );
  }

  // Lobby UI removed - now in separate Lobby component
  // Room options UI removed - now handled in Dashboard
  // All lobby and room creation UI code has been moved to Lobby component and Dashboard

  // Render game table - all lobby and room creation UI has been removed

  const isStanding = mainPlayerId ? standingSecondsByPlayer[mainPlayerId] !== undefined : false;
  const openLobbyWindow = () => {
    const rId = roomId || searchParams.get('roomId') || localStorage.getItem('currentRoomId');
    if (!rId) return;
    const url = `${window.location.origin}/lobby?roomId=${rId}`;
    const w = window.open(url, '_blank', 'noopener,noreferrer,width=1000,height=700,resizable=yes,scrollbars=yes');
    if (w) {
      lobbyWindowRef.current = w;
      w.focus();
    } else {
      setErrorModal({ message: 'Please allow popups to open the lobby, or try again after allowing popups for this site.' });
    }
  };
  const handleLeaveGameClick = () => {
    const rId = roomId || searchParams.get('roomId') || localStorage.getItem('currentRoomId');
    const pid = mainPlayerId || localStorage.getItem('userId');
    if (socketServiceRef.current && rId && pid) {
      socketServiceRef.current.sendMessage({ type: UserMessages.EXIT_GAME, roomId: rId, playerId: pid });
      socketServiceRef.current.sendMessage({ type: UserMessages.LEAVE_ROOM, roomId: rId, playerId: pid });
      socketServiceRef.current.disconnect();
      socketServiceRef.current = null;
    }
    localStorage.removeItem('currentRoomId');
    // Close lobby: either opened from game (lobbyWindowRef) or we are the game popup (opener is lobby)
    if (lobbyWindowRef.current && !lobbyWindowRef.current.closed) {
      lobbyWindowRef.current.close();
      lobbyWindowRef.current = null;
    } else if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
      window.opener.close();
    }
    window.close();
    // If we're in the main tab, close() may be no-op; go to dashboard
    setTimeout(() => {
      if (typeof window !== 'undefined' && !window.closed) {
        navigate('/dashboard');
      }
    }, 100);
  };
  const handleStandSitClick = () => {
    const rId = roomId || searchParams.get('roomId') || localStorage.getItem('currentRoomId');
    const pid = mainPlayerId || localStorage.getItem('userId');
    if (!socketServiceRef.current || !rId || !pid) return;
    if (isStanding) {
      socketServiceRef.current.sendMessage({ type: UserMessages.PLAYER_SIT, roomId: rId, playerId: pid });
    } else {
      socketServiceRef.current.sendMessage({ type: UserMessages.PLAYER_STAND, roomId: rId, playerId: pid });
    }
    setDrawerOpen(false);
  };

  const openGameChat = () => {
    setChatPanelOpen(true);
    setChatPanelMinimized(false);
    setDrawerOpen(false);
  };

  const handleSendGameChat = () => {
    const text = gameChatInput.trim();
    if (!text || !socketServiceRef.current?.isConnected()) return;
    const rId = roomId || searchParams.get('roomId') || localStorage.getItem('currentRoomId');
    const pid = mainPlayerId || localStorage.getItem('userId');
    const playerName = localStorage.getItem('username') || 'Player';
    if (!rId || !pid) return;
    socketServiceRef.current.sendMessage({
      type: UserMessages.CHAT_MESSAGE_GAME,
      roomId: rId,
      playerId: pid,
      playerName,
      message: text,
    });
    setGameChatInput('');
  };

  const hasSeat = players.some((p) => p.uuid === mainPlayerId);
  const canObserverJoinSeat = isObserver && !hasSeat;

  return (
    <div className="game-container">
      {errorModal && (
        <div className="game-error-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="game-error-modal-title">
          <div className="game-error-modal">
            <div className="game-error-modal-header">
              <h2 id="game-error-modal-title" className="game-error-modal-title">Message</h2>
              <button type="button" className="game-error-modal-close" onClick={closeErrorModal} aria-label="Close">×</button>
            </div>
            <p className="game-error-modal-message">{errorModal.message}</p>
            <div className="game-error-modal-actions">
              <button type="button" className="game-error-modal-btn" onClick={closeErrorModal}>OK</button>
            </div>
          </div>
        </div>
      )}
      <div className="game-fullscreen-wrapper">
        <button
          type="button"
          className="game-fullscreen-btn"
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'}
          aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
        >
          {isFullscreen ? '⤢' : '⛶'}
        </button>
      </div>
      <div className="game-drawer-wrapper">
        <button type="button" className="game-drawer-toggle" onClick={() => setDrawerOpen((o) => !o)} aria-label="Menu">⋮</button>
        {drawerOpen && (
          <div className="game-drawer">
            <button type="button" className="game-drawer-btn" onClick={openLobbyWindow}>Lobby</button>
            <button type="button" className="game-drawer-btn" onClick={openGameChat}>Chat</button>
            <button type="button" className="game-drawer-btn" onClick={handleStandSitClick}>{isStanding ? 'Sit' : 'Stand'}</button>
            <label className="game-drawer-toggle-reveal">
              <input
                type="checkbox"
                checked={showRevealModalEnabled}
                onChange={(e) => {
                  const v = e.target.checked;
                  setShowRevealModalEnabled(v);
                  try {
                    localStorage.setItem(REVEAL_MODAL_ENABLED_STORAGE_KEY, String(v));
                  } catch { /* ignore */ }
                }}
              />
              <span>Enable Hand Reveal</span>
            </label>
            <label className="game-drawer-toggle-reveal">
              <input
                type="checkbox"
                checked={showTableRevealEnabled}
                onChange={(e) => {
                  const v = e.target.checked;
                  setShowTableRevealEnabled(v);
                  try {
                    localStorage.setItem(TABLE_REVEAL_ENABLED_STORAGE_KEY, String(v));
                  } catch { /* ignore */ }
                }}
              />
              <span>Enable Table Reveal</span>
            </label>
            <label className="game-drawer-toggle-reveal">
              <input
                type="checkbox"
                checked={turnSoundEnabled}
                onChange={(e) => {
                  const v = e.target.checked;
                  setTurnSoundEnabled(v);
                  try {
                    localStorage.setItem(TURN_SOUND_ENABLED_STORAGE_KEY, String(v));
                  } catch { /* ignore */ }
                }}
              />
              <span>Enable turn sound</span>
            </label>
            <label className="game-drawer-toggle-reveal">
              <input
                type="checkbox"
                checked={isFullscreen}
                onChange={() => toggleFullscreen()}
              />
              <span>Full screen</span>
            </label>
            <div className="game-drawer-observers">
              <button
                type="button"
                className="game-drawer-observers-header"
                onClick={() => setObserversListOpen((o) => !o)}
                aria-expanded={observersListOpen}
              >
                <span>Observers ({observers.length})</span>
                <span aria-hidden>{observersListOpen ? '▼' : '▶'}</span>
              </button>
              {observersListOpen && (
                <ul className="game-drawer-observers-list">
                  {observers.length === 0 ? (
                    <li className="game-drawer-observers-empty">No observers</li>
                  ) : (
                    observers.map((o) => (
                      <li key={o.playerId} className="game-drawer-observers-item">{o.playerName}</li>
                    ))
                  )}
                </ul>
              )}
            </div>
            <button type="button" className="game-drawer-btn game-drawer-leave" onClick={handleLeaveGameClick}>Leave game</button>
          </div>
        )}
      </div>

      {/* Game chat panel: separate position/size for minimized vs expanded; drag icons top-left and top-right */}
      {chatPanelOpen && (
        <div
          ref={chatPanelRef}
          className={`game-chat-panel ${chatPanelMinimized ? 'game-chat-minimized' : 'game-chat-expanded'}`}
          style={{
            left: chatPanelMinimized ? chatPanelPosMinimized.left : chatPanelPosExpanded.left,
            top: chatPanelMinimized ? chatPanelPosMinimized.top : chatPanelPosExpanded.top,
          }}
        >
          {chatPanelMinimized ? (
            <>
              <button
                type="button"
                className="game-chat-minimized-bar"
                onClick={() => {
                  if (chatJustDraggedRef.current) {
                    chatJustDraggedRef.current = false;
                    return;
                  }
                  setChatPanelMinimized(false);
                  setChatHasUnread(false);
                }}
                title="Click to open chat"
              >
                <span className="game-chat-minimized-icon" aria-hidden>💬</span>
                {chatHasUnread && <span className="game-chat-unread-dot" aria-label="Unread messages" />}
              </button>
              <div
                className="game-chat-panel-drag-handle game-chat-panel-drag-handle--right"
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDragStart('chat', e.clientX, e.clientY); }}
                title="Drag to move"
              >
                <span className="game-chat-panel-drag-icon" aria-hidden>⋮⋮</span>
              </div>
            </>
          ) : (
            <>
              <div className="game-chat-header">
                <h3 className="game-chat-title">Game Chat</h3>
                <div className="game-chat-header-actions">
                  <button type="button" className="game-chat-btn game-chat-minimize-btn" onClick={() => setChatPanelMinimized(true)} title="Minimize">−</button>
                  <button type="button" className="game-chat-btn game-chat-close-btn" onClick={() => setChatPanelOpen(false)} title="Close">×</button>
                </div>
              </div>
              <div
                className="game-chat-panel-drag-handle game-chat-panel-drag-handle--left"
                onPointerDown={(e) => { e.preventDefault(); handleDragStart('chat', e.clientX, e.clientY); }}
                title="Drag to move"
              >
                <span className="game-chat-panel-drag-icon" aria-hidden>⋮⋮</span>
              </div>
              <div
                className="game-chat-panel-drag-handle game-chat-panel-drag-handle--right"
                onPointerDown={(e) => { e.preventDefault(); handleDragStart('chat', e.clientX, e.clientY); }}
                title="Drag to move"
              >
                <span className="game-chat-panel-drag-icon" aria-hidden>⋮⋮</span>
              </div>
              <div className="game-chat-messages" ref={gameChatMessagesRef}>
                {gameChatMessages.length === 0 ? (
                  <p className="game-chat-empty">No messages yet. Say something!</p>
                ) : (
                  gameChatMessages.map((msg, idx) => (
                    <div key={idx} className="game-chat-message">
                      <span className="game-chat-player" style={{ color: colorForChatName(msg.playerName) }}>{msg.playerName}:</span>
                      <span className="game-chat-text">{msg.message}</span>
                    </div>
                  ))
                )}
              </div>
              <div className="game-chat-input-wrap">
                <div className="game-input-with-icon">
                  <input
                    type="text"
                    className="game-chat-input"
                    value={gameChatInput}
                    onChange={(e) => setGameChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendGameChat()}
                    placeholder="Type a message..."
                    maxLength={300}
                  />
                  <span className="game-input-keyboard-icon" aria-hidden>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0-2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25v-.008zm0-2.25h.008v.008H8.25V8.25zm.75 4.5h.008v.008H9v-.008zm0-2.25h.008v.008H9V13.5zm0-2.25h.008v.008H9v-.008zm0 2.25h.008v.008H9V13.5zm2.25-4.5h.008v.008h.008V8.25zm0 2.25h.008v.008h.008V13.5zm0-2.25h.008v.008h.008v-.008zm0 2.25h.008v.008h.008V13.5zm2.25-4.5h.008v.008h.008V8.25zm0 2.25h.008v.008h.008V13.5z" />
                    </svg>
                  </span>
                </div>
                <button type="button" className="game-chat-send-btn" onClick={handleSendGameChat}>Send</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Reveal cards modal: shown when hand ended for main player (including if they folded); draggable; can be disabled in drawer */}
      {showRevealModalEnabled && winMessage && mainPlayerId && (() => {
        const mainPlayer = players.find((p) => p.uuid === mainPlayerId);
        const mainHand = mainPlayer?.hand ?? [];
        const hasCards = mainHand.length > 0;
        const rev0 = revealedCardsByPlayer[mainPlayerId]?.[0];
        const rev1 = revealedCardsByPlayer[mainPlayerId]?.[1];
        const canRevealAny = hasCards && (!rev0 || !rev1);
        const alreadyRevealedAtShowdown = showdownCardsShownThisHand && mainPlayer && !mainPlayer.isFolded;
        if (!hasCards || !canRevealAny || alreadyRevealedAtShowdown) return null;
        return (
          <div
            ref={revealModalRef}
            className="reveal-cards-modal"
            style={{ left: revealModalPos.left, top: revealModalPos.top }}
          >
            <div className="reveal-cards-modal-header">
              <h3 className="reveal-cards-modal-title">Reveal your cards</h3>
            </div>
            <div
              className="reveal-cards-modal-drag-handle reveal-cards-modal-drag-handle--left"
              onPointerDown={(e) => { e.preventDefault(); handleDragStart('reveal', e.clientX, e.clientY); }}
              title="Drag to move"
            >
              <span className="reveal-cards-modal-drag-icon" aria-hidden>⋮⋮</span>
            </div>
            <div
              className="reveal-cards-modal-drag-handle reveal-cards-modal-drag-handle--right"
              onPointerDown={(e) => { e.preventDefault(); handleDragStart('reveal', e.clientX, e.clientY); }}
              title="Drag to move"
            >
              <span className="reveal-cards-modal-drag-icon" aria-hidden>⋮⋮</span>
            </div>
            <div className="reveal-cards-modal-body">
              <div className="reveal-cards-modal-cards">
                {[0, 1].map((i) => {
                  const card = rev0 && i === 0 ? rev0 : rev1 && i === 1 ? rev1 : mainHand[i];
                  return (
                    <div key={i} className="reveal-cards-modal-card-slot">
                      <img
                        src={getSafeCardImagePath(card ?? { rank: 0, suit: '?', isFaceUp: false })}
                        alt={card ? `${card.rank}-${card.suit}` : 'Card back'}
                        className="reveal-cards-modal-card"
                      />
                    </div>
                  );
                })}
              </div>
              <div className="reveal-cards-modal-actions">
                {!rev0 && (
                  <button type="button" className="reveal-cards-modal-btn" onClick={() => sendRevealCard(0)}>
                    Reveal card 1
                  </button>
                )}
                {!rev1 && (
                  <button type="button" className="reveal-cards-modal-btn" onClick={() => sendRevealCard(1)}>
                    Reveal card 2
                  </button>
                )}
                {!rev0 && !rev1 && (
                  <button
                    type="button"
                    className="reveal-cards-modal-btn reveal-cards-modal-btn-both"
                    onClick={() => { sendRevealCard(0); sendRevealCard(1); }}
                  >
                    Reveal both
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {gameHeldMessage && (
        <div className="game-held-message">
          {gameHeldMessage}
        </div>
      )}

      {tableClosingAfterHandMessage && (
        <div className="table-closing-after-hand-banner" role="alert">
          {tableClosingAfterHandMessage}
        </div>
      )}

      {/* Join seat modal: observer clicks empty seat → choose chips → join */}
      {joinSeatModal && (
        <div className="join-seat-modal-overlay" onClick={() => setJoinSeatModal(null)} role="presentation">
          <div className="join-seat-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="join-seat-modal-title">Join at seat {joinSeatModal.seatIndex + 1}</h3>
            <p className="join-seat-modal-label">Chips (${tableMinChips} – ${tableMaxChips})</p>
            <div className="game-input-with-icon join-seat-modal-input-wrap">
              <input
                type="number"
                min={tableMinChips}
                max={tableMaxChips}
                value={joinSeatChips}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v)) setJoinSeatChips(Math.max(tableMinChips, Math.min(tableMaxChips, v)));
                }}
                className="join-seat-modal-input"
              />
              <span className="game-input-keyboard-icon" aria-hidden>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0-2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25v-.008zm0-2.25h.008v.008H8.25V8.25zm.75 4.5h.008v.008H9v-.008zm0-2.25h.008v.008H9V13.5zm0-2.25h.008v.008H9v-.008zm0 2.25h.008v.008H9V13.5zm2.25-4.5h.008v.008h.008V8.25zm0 2.25h.008v.008h.008V13.5zm0-2.25h.008v.008h.008v-.008zm0 2.25h.008v.008h.008V13.5zm2.25-4.5h.008v.008h.008V8.25zm0 2.25h.008v.008h.008V13.5z" />
                </svg>
              </span>
            </div>
            <div className="join-seat-modal-actions">
              <button type="button" className="join-seat-modal-btn join-seat-modal-cancel" onClick={() => setJoinSeatModal(null)}>Cancel</button>
              <button
                type="button"
                className="join-seat-modal-btn join-seat-modal-confirm"
                onClick={() => {
                  sendJoinAsPlayer(joinSeatModal.seatIndex, joinSeatChips);
                  setJoinSeatModal(null);
                }}
              >
                Join
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action bar as direct child of game-container so it stacks above chat (same level, higher z-index) */}
      {isMyTurn && (() => {
            const mainPlayer = players.find((p) => p.isMainPlayer);
            const mainPlayerChips = mainPlayer?.chips ?? 0;
            const amountToCall = highestBet - playerLastRoundBet;
            const callIsAllIn = amountToCall > 0 && amountToCall >= mainPlayerChips;
            return (
        <div
          ref={actionBarRef}
          className="action-buttons-wrapper"
          style={{ left: actionBarPos.left, top: actionBarPos.top }}
        >
          <div
            className="action-buttons-drag-handle action-buttons-drag-handle--left"
            onPointerDown={(e) => { e.preventDefault(); handleDragStart('action', e.clientX, e.clientY); }}
            title="Drag to move"
          >
            <span className="action-buttons-drag-icon" aria-hidden>⋮⋮</span>
          </div>
          <motion.div
            className="action-buttons"
            layout={false}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <button onClick={handleFold} className="action-btn fold-btn">
              Fold
            </button>
            <button onClick={handleCall} className="action-btn call-btn">
              {(noBetYet || amountToCall === 0) ? 'Check' : callIsAllIn ? `All In $${mainPlayerChips}` : `Call $${amountToCall}`}
            </button>
            <div className="raise-controls">
              {callIsAllIn ? null : minRaiseAmount > 0 && maxRaiseAmount > minRaiseAmount ? (
                <>
                  <div className="raise-controls-slider-row">
                    <button
                      type="button"
                      className="action-btn action-btn-small raise-controls-minus"
                      onClick={() => setRaiseAmount((p) => Math.max(minRaiseAmount, p - 1))}
                      disabled={raiseAmount <= minRaiseAmount}
                      aria-label="Decrease raise"
                    >−</button>
                    <input
                      type="range"
                      min={minRaiseAmount}
                      max={maxRaiseAmount}
                      step={1}
                      value={raiseAmount}
                      onChange={(e) => setRaiseAmount(Number(e.target.value))}
                    />
                    <button
                      type="button"
                      className="action-btn action-btn-small raise-controls-plus"
                      onClick={() => setRaiseAmount((p) => Math.min(maxRaiseAmount, p + 1))}
                      disabled={raiseAmount >= maxRaiseAmount}
                      aria-label="Increase raise"
                    >+</button>
                  </div>
                  <span className="raise-controls-value">${raiseAmount}</span>
                  <button onClick={handleRaise} className="action-btn raise-btn">
                    {raiseAmount >= maxRaiseAmount ? 'All In' : (noBetYet ? 'Bet' : 'Raise')}
                  </button>
                  <div className="raise-controls-presets">
                    {(() => {
                      const bigBlind = gameState?.config?.bigBlind ?? 20;
                      const halfPot = Math.floor(pot / 2);
                      const presets = [
                        { label: '3B', value: 3 * bigBlind },
                        { label: '4B', value: 4 * bigBlind },
                        { label: '½ Pot', value: halfPot },
                        { label: 'Pot', value: pot },
                      ];
                      return presets.map(({ label, value }) => {
                        const target = Math.max(minRaiseAmount, Math.min(maxRaiseAmount, value));
                        const disabled = value > maxRaiseAmount;
                        return (
                          <button
                            key={label}
                            type="button"
                            className="action-btn action-btn-preset"
                            disabled={disabled}
                            onClick={() => setRaiseAmount(target)}
                            title={disabled ? 'Not enough chips' : `Set to $${target}`}
                          >
                            {label}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </>
              ) : (
                <>
                  <span>${mainPlayerChips}</span>
                  <button onClick={handleRaise} className="action-btn raise-btn">
                    All In
                  </button>
                </>
              )}
            </div>
          </motion.div>
          <div
            className="action-buttons-drag-handle action-buttons-drag-handle--right"
            onPointerDown={(e) => { e.preventDefault(); handleDragStart('action', e.clientX, e.clientY); }}
            title="Drag to move"
          >
            <span className="action-buttons-drag-icon" aria-hidden>⋮⋮</span>
          </div>
        </div>
            );
      })()}
      <div className="poker-table">
        {/* Table cards: 5 slots; during winner celebration show face-down placeholders with Reveal button overlay for remaining cards */}
        <div className="table-cards-container">
          <div className="pot-display">Pot: ${pot}</div>
          <div className="table-cards-row">
            <div className="table-cards">
              {[0, 1, 2, 3, 4].map((index) => {
                const card = tableCards[index];
                const isPlaceholder = !card && showTableRevealEnabled && showRevealTableCardsOption && index >= tableCards.length;
                const cardKey = card ? `table-${card.rank}-${card.suit}-${index}` : `table-placeholder-${index}`;
                const cardFormat = card ? cardToBestHandFormat(card) : '';
                const isWinningCard = card ? winningCards.includes(cardFormat) : false;
                if (card) {
                  return (
                    <div key={cardKey} className="table-card-slot">
                      <motion.img
                        src={getSafeCardImagePath(card)}
                        alt={`${card.rank}-${card.suit}`}
                        className={`table-card ${isWinningCard ? 'winning-card' : ''}`}
                        layout={false}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1, y: isWinningCard ? 0 : 20 }}
                        transition={{ duration: 0.2, delay: Math.min(index * 0.1, 0.5) }}
                      />
                    </div>
                  );
                }
                if (isPlaceholder) {
                  return (
                    <div key={cardKey} className="table-card-slot">
                      <div className="table-card-placeholder-wrapper">
                        <img
                          src={getSafeCardImagePath({ rank: 0, suit: '?', isFaceUp: false })}
                          alt="Card back"
                          className="table-card table-card-placeholder"
                        />
                      </div>
                    </div>
                  );
                }
                return <div key={cardKey} className="table-card-slot" />;
              })}
              {showTableRevealEnabled && tableCards.length < 5 && showRevealTableCardsOption && (() => {
                const placeholderCount = 5 - tableCards.length;
                const isNarrowViewport = typeof window !== 'undefined' && window.innerWidth <= 900;
                const slotWidth = isNarrowViewport ? 53 : 80;
                const gap = 8;
                const wrapperWidth = placeholderCount * slotWidth + Math.max(0, placeholderCount - 1) * gap;
                return (
                <div
                  className="table-cards-reveal-wrapper"
                  style={{ width: wrapperWidth }}
                >
                  <button
                    type="button"
                    className="table-cards-reveal-btn"
                    onClick={sendRevealRemainingTableCards}
                    title="Reveal remaining table cards"
                    aria-label="Reveal remaining table cards"
                  >
                    <span className="table-cards-reveal-btn-icon" aria-hidden>
                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </span>
                    <span className="table-cards-reveal-btn-label">Reveal</span>
                  </button>
                </div>
                );
              })()}
            </div>
          </div>
          {/* Win message under table cards */}
          {winMessage && (
            <motion.div
              key="win-message"
              className="win-message-table"
              layout={false}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {String(winMessage).slice(0, 200)}
            </motion.div>
          )}
          {notEnoughPlayersMessage && (
            <motion.div
              className="not-enough-players-message"
              layout={false}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {notEnoughPlayersMessage}
            </motion.div>
          )}
        </div>

        {/* Seats around the table */}
        <div className="seats-container">
          {seats.map((seat, index) => {
       
            // Find player by seatIndex to ensure we always have the latest player object
            const player = players.find((p) => p.seatIndex === seat.seatIndex);
            const isCurrentPlayer = currentPlayerIndex === seat.seatIndex;

            return (
              <div
                key={index}
                className={`seat seat-${index} ${isCurrentPlayer ? 'active' : ''}`}
              >
                {seat.isOccupied && player ? (
                  <div style={{position: 'relative'}}>
                    {isMobileWindowSize && (
                      <div style={{ position: 'relative' }}>
                        {(player.uuid === mainPlayerId
                          ? ((player.hand?.length ?? 0) > 0 || revealedCardsByPlayer[player.uuid]?.[0] || revealedCardsByPlayer[player.uuid]?.[1])
                          : (!!revealedCardsByPlayer[player.uuid]?.[0] || !!revealedCardsByPlayer[player.uuid]?.[1] || (!player.isFolded && ((player.hand?.length ?? 0) > 0)))) && (
                          <div className={`player-cards ${player.isFolded && player.uuid === mainPlayerId ? 'player-cards-folded' : ''}`}>
                            {getDisplayCardsForPlayer(player).map((displayCard, cardIndex) => {
                              const cardFormat = displayCard ? cardToBestHandFormat(displayCard) : '';
                              const isWinningCard = winningPlayerId === player.uuid && displayCard && winningCards.includes(cardFormat);
                              const isUserRevealed = !!userRevealedCardsByPlayer[player.uuid]?.[cardIndex];
                              const isRevealedByMe = player.uuid === mainPlayerId && isUserRevealed;
                              const isRevealedByOther = isUserRevealed && !isRevealedByMe;
                              const revealedClass = isRevealedByMe ? 'player-card-revealed-by-me' : isRevealedByOther ? 'player-card-revealed-other' : '';
                              return (
                                <img
                                  key={`${player.uuid}-card-${cardIndex}`}
                                  src={getSafeCardImagePath(displayCard ?? { rank: 0, suit: '?', isFaceUp: false })}
                                  alt={displayCard ? `${displayCard.rank}-${displayCard.suit}` : 'Card back'}
                                  className={`player-card ${isWinningCard ? 'winning-card' : ''} ${revealedClass}`}
                                  style={isWinningCard || revealedClass ? {} : { transform: 'translateY(10px) translateZ(0)' }}
                                />
                              );
                            })}
                          </div>
                        )}
                        <div className={`player-info ${isCurrentPlayer ? 'player-info-active' : ''} ${winningPlayerId === player.uuid ? 'winning-card' : ''}`}>
                          <div className="display-flex justify-content-between flex-row align-items-center column-gap-10">

                            <div className="display-flex flex-column align-items-left row-gap-5">
                              <div className="player-name">{player.name}</div>
                              <div className="player-chips">${player.chips}</div>
                              {standingSecondsByPlayer[player.uuid] !== undefined && (
                                <div className="seat-standing-badge">
                                  Standing · {Math.floor((standingSecondsByPlayer[player.uuid] ?? 0) / 60)}:{(standingSecondsByPlayer[player.uuid] ?? 0) % 60 < 10 ? '0' : ''}{(standingSecondsByPlayer[player.uuid] ?? 0) % 60}
                                </div>
                              )}
                              {disconnectGraceSecondsByPlayer[player.uuid] !== undefined && (
                                <div className="seat-standing-badge seat-disconnect-badge">
                                  Disconnected · {Math.floor((disconnectGraceSecondsByPlayer[player.uuid] ?? 0) / 60)}:{(disconnectGraceSecondsByPlayer[player.uuid] ?? 0) % 60 < 10 ? '0' : ''}{(disconnectGraceSecondsByPlayer[player.uuid] ?? 0) % 60}
                                </div>
                              )}
                            </div>


                          </div>

                        </div>

                        {isCurrentPlayer && (
                          <div className={`timer-indicator timer-indicator-${index}`}>
                            <div className="timer-bar" style={{ width: `${(timer / 30) * 100}%` }} />
                            <span>{timer}s</span>
                          </div>
                        )}
                      </div>
                    )}

                    {(isDesktopWindowSize || isTabletWindowSize) && (
                      <div style={{ position: 'relative' }}>
                        {(player.uuid === mainPlayerId
                          ? ((player.hand?.length ?? 0) > 0 || revealedCardsByPlayer[player.uuid]?.[0] || revealedCardsByPlayer[player.uuid]?.[1])
                          : (!!revealedCardsByPlayer[player.uuid]?.[0] || !!revealedCardsByPlayer[player.uuid]?.[1] || (!player.isFolded && ((player.hand?.length ?? 0) > 0)))) && (
                          <div className={`player-cards ${player.isFolded && player.uuid === mainPlayerId ? 'player-cards-folded' : ''}`}>
                            {getDisplayCardsForPlayer(player).map((displayCard, cardIndex) => {
                              const cardFormat = displayCard ? cardToBestHandFormat(displayCard) : '';
                              const isWinningCard = winningPlayerId === player.uuid && displayCard && winningCards.includes(cardFormat);
                              const isUserRevealed = !!userRevealedCardsByPlayer[player.uuid]?.[cardIndex];
                              const isRevealedByMe = player.uuid === mainPlayerId && isUserRevealed;
                              const isRevealedByOther = isUserRevealed && !isRevealedByMe;
                              const revealedClass = isRevealedByMe ? 'player-card-revealed-by-me' : isRevealedByOther ? 'player-card-revealed-other' : '';
                              return (
                                <img
                                  key={`${player.uuid}-card-${cardIndex}`}
                                  src={getSafeCardImagePath(displayCard ?? { rank: 0, suit: '?', isFaceUp: false })}
                                  alt={displayCard ? `${displayCard.rank}-${displayCard.suit}` : 'Card back'}
                                  className={`player-card ${isWinningCard ? 'winning-card' : ''} ${revealedClass}`}
                                  style={isWinningCard || revealedClass ? {} : { transform: 'translateY(10px) translateZ(0)' }}
                                />
                              );
                            })}
                          </div>
                        )}
                        <div className={`player-info ${isCurrentPlayer ? 'player-info-active' : ''} ${winningPlayerId === player.uuid ? 'winning-card' : ''}`}>
                          <div className="display-flex justify-content-between flex-row align-items-center column-gap-10">

                            <div className="display-flex flex-column align-items-left row-gap-5">
                              <div className="player-name">{player.name}</div>
                              <div className="player-chips">${player.chips}</div>
                              {standingSecondsByPlayer[player.uuid] !== undefined && (
                                <div className="seat-standing-badge">
                                  Standing · {Math.floor((standingSecondsByPlayer[player.uuid] ?? 0) / 60)}:{(standingSecondsByPlayer[player.uuid] ?? 0) % 60 < 10 ? '0' : ''}{(standingSecondsByPlayer[player.uuid] ?? 0) % 60}
                                </div>
                              )}
                              {disconnectGraceSecondsByPlayer[player.uuid] !== undefined && (
                                <div className="seat-standing-badge seat-disconnect-badge">
                                  Disconnected · {Math.floor((disconnectGraceSecondsByPlayer[player.uuid] ?? 0) / 60)}:{(disconnectGraceSecondsByPlayer[player.uuid] ?? 0) % 60 < 10 ? '0' : ''}{(disconnectGraceSecondsByPlayer[player.uuid] ?? 0) % 60}
                                </div>
                              )}
                            </div>


                          </div>
                          
                        </div>

                        {isCurrentPlayer && (
                          <div className={`timer-indicator timer-indicator-${index}`}>
                            <div className="timer-bar" style={{ width: `${(timer / 30) * 100}%` }} />
                            <span>{timer}s</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Action text display (also for desktop/tablet) */}
                    {seat.actionText && (
                      <div className={`seat-action-text seat-action-${index}`}>
                        {seat.actionText}
                      </div>
                    )}
                    {seat.isButton && (
                      <img
                        src={require('../../assets/images/tableImages/buttonImage.png').default || require('../../assets/images/tableImages/buttonImage.png')}
                        alt="Button"
                        className={`button-indicator-${index}`}
                      />
                    )}
                    {seat.isSmallBlind && (
                      <img
                        src={require('../../assets/images/tableImages/smallBlindImage.png').default || require('../../assets/images/tableImages/smallBlindImage.png')}
                        alt="Small Blind"
                        className={`blind-indicator-${index}`}
                      />
                    )}
                    {seat.isBigBlind && (
                      <img
                        src={require('../../assets/images/tableImages/bigBlindImage.png').default || require('../../assets/images/tableImages/bigBlindImage.png')}
                        alt="Big Blind"
                        className={`blind-indicator-${index}`}
                      />
                    )}
                  </div>
                ) : (
                  <div
                    className={`empty-seat ${canObserverJoinSeat ? 'empty-seat-clickable' : ''} ${canObserverJoinSeat && showObserverJoinGlow ? 'empty-seat-observer-glow' : ''}`}
                    role={canObserverJoinSeat ? 'button' : undefined}
                    tabIndex={canObserverJoinSeat ? 0 : undefined}
                    onClick={canObserverJoinSeat ? () => { setJoinSeatChips(tableMinChips); setJoinSeatModal({ seatIndex: seat.seatIndex }); } : undefined}
                    onKeyDown={canObserverJoinSeat ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setJoinSeatChips(tableMinChips); setJoinSeatModal({ seatIndex: seat.seatIndex }); } } : undefined}
                  >
                    {canObserverJoinSeat ? 'Click to join' : 'Empty'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Game;

